import React, {
	CSSProperties,
	Fragment,
	MutableRefObject,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import videojs from 'video.js';
import Draggable from 'react-draggable';
import Fade from '@mui/material/Fade';
import { Dispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import audioPlaying from '../../animation/audioPlaying.json';
import {
	debounce,
	find,
	findIndex,
	findLast,
	first,
	flatten,
	get,
	includes,
	isEmpty,
	isFinite,
	isFunction,
	isNumber,
	isString,
	last,
	map,
	max,
	min,
	omit,
	sortBy,
	upperCase,
} from 'lodash';
import {
	Box,
	Button,
	IconButton,
	Popper,
	PopperProps,
	TextareaAutosize,
	Tooltip,
} from '@mui/material';
import Konva from 'konva';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import 'video.js/dist/video-js.css';
import './VideoPlayer.css';
import Video from './Video';
import Annotations from '../Annotations/Annotations';
import Amplitude from '../Amplitude/Amplitude';
import {
	AnnotationMetaSnapshot,
	AnnotationSnapshot,
	CropAreaType,
} from '../Annotations/Annotations.types';
import {
	addAnnotationsAction,
	getAnnotationsByTime,
	sendAnnotationAction,
	setAllowComment,
	setAnnotationsByTime,
	setAnnotationToolAction,
	setAppComment,
	setScrollTo,
	setTrimFirstPlay,
	skipCta,
	trackClickAction,
	trackCtaAction,
	trackHotspotAction,
	trackViewCountAction,
	updateAnnotationAction,
} from '../../Pages/Flo/Flo.reducers';
import classNames from 'classnames';
import {
	floDuration as floDurationSelector,
	floSpaceIdSelector,
} from '../../Pages/Flo/Flo.selectors';
import {
	currentProfileSelector,
	headerBrandImageSelector,
} from '../../Common/Common.selector';
import { RootState } from '../../store';
import { Profile } from '../../Containers/Routes/Routes.types';
import { scaleLinear } from 'd3-scale';
import {
	calculateAspectRatio,
	calculateAspectRatioFit,
	calculateVideoCropMatrix,
} from '../../utils/video.utils';
import {
	BackgroundMusicResourceType,
	CropStateType,
	elementsDataType,
	HotSpotType,
	pauseTimeType,
	publishElementsDataType,
	stepsElementType,
} from '../../Pages/Flo/Flo.types';
import {
	hideDialogAction,
	showDialogAction,
	updateDialogAction,
} from '../VideoDialog/VideoDialog.reducer';
import { ReactComponent as BlackCloseIcon } from '../../Common/images/black-close.svg';
import {
	DIMENSIONS_ERROR_MESSAGE,
	FLO_ELEMENT_BLUR_TYPE,
	FLO_ELEMENT_TEXT_TYPE,
	FLO_ELEMENT_SPOTLIGHT_TYPE,
	FLO_START_BUTTON_TEXT,
	PUBLISHED_FLO_COMMENT_PLACEHOLDER_EMAIL,
	PUBLISHED_FLO_COMMENT_PLACEHOLDER_NAME,
	PUBLISHED_FLO_COMMENT_PLACEHOLDER_QUESTION,
} from '../../Common/Common.constants';
import { setNotification } from '../Notification/Toasts.reducers';
import mouseAnimation from './mouse-animation.json';
import Cookie from 'js-cookie';
import { Player } from '@lottiefiles/react-lottie-player';
import { checkTabletIpadPlatform } from '../../utils/IsPhone';
import {
	getElementVoiceResources,
	getWindowDimensions,
	isMicroApp,
	isSwiftPlay,
	preventDefaultForTouchEndEvent,
	removeFloikLoaderInMicroApp,
	sanitizeHtml,
	sanitizeHtmlLinks,
	Subtitle,
	useBackgroundMusic,
	useElementVoicePlayer,
} from '../../Common/Common.utils';
import CustomInVideoControls from './CustomInVideoControls/CustomInVideoControls';
import DialogComponent from '../VideoDialog/VideoDialog';
import {
	CLICK_ACTIVITY_BORDER_SIZE,
	MAX_MOBILE_VIEW_HEIGHT_IN_LANDSCAPE,
	MAX_MOBILE_VIEW_WIDTH,
	MAX_TABLET_VIEW_WIDTH,
	MOBILE_VIEW_CLICK_ACTIVITY_BORDER_SIZE,
} from '../../Common/Common.params';
import { analyticsLog } from '../../store/reducers/Log.reducer';
import {
	findTooltipDefaultPosition,
	getAdventureCtaDialogProperties,
	useTrackCompletionStatus,
} from './VideoPlayer.utils';
import BrandImage from '../../Pages/Flo/Components/BrandImage/BrandImage';
import { ImageElement } from '../ImageElement/ImageElement';
import VideoBlurs from './VideoBlurs/VideoBlurs';
import { setAutoPlay } from '../../Pages/Flo/Flo.actions';
import VideoSpotlights from './VideoSpotlights/VideoSpotlights';
import { IconRotate2, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import KonvaEventObject = Konva.KonvaEventObject;
import styles from './VideoPlayer.module.css';
import { TextElement } from '../TextElement/TextElement';
import TextElementShowUntil from '../TextElement/TextElementShowUntil';
import Wrapper from '../../Pages/Flo/pages/GuideFlo/Instructions/Wrapper';

const analyticsMetricsHelper = {
	hasManualSeekedForward: false,
};

const defaultControls: videojs.PlayerOptions = {
	controls: false,
	fluid: false,
};

const DEFAULT_PRESENTER_OPTIONS = {
	...defaultControls,
	width: 300,
	height: 200,
};

const presenterBaseStyles: CSSProperties = {
	position: 'absolute',
	left: 0,
	top: 0,
};
const isTouchDevice = checkTabletIpadPlatform();
const validProtocols = ['http', 'https', 'ftp', 'ssh', 'telnet', 'sftp', 'file', 'data'];

const pattern = new RegExp('^(' + validProtocols.join('|') + ')://');

const floElements = {};

type ActivityType = {
	id: string;
	hasActivity: boolean;
	hasPrev?: boolean;
	enableZoom?: boolean;
	pointerPosition: {
		x: number;
		y: number;
	};
	values?: {
		title: string;
		description: string;
	};
	hotspotType?: string;
	elementBounds: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	windowSize: {
		width: number;
		height: number;
	};
	text: string;
	position?: string;
};

const getPopperAnimationName = (position: string) => {
	if (!isString(position)) {
		return '';
	}
	if (position.includes('left')) {
		return 'moveInFromLeft';
	} else if (position.includes('right')) {
		return 'moveInFromRight';
	} else if (position.includes('top')) {
		return 'moveInFromTop';
	} else if (position.includes('bottom')) {
		return 'moveInFromBottom';
	}
	return '';
};

const ClickEventActivity = React.memo(
	({
		activity,
		width,
		height,
		offsetWidth,
		offsetHeight,
		isCropped,
		isSeeking,
		isCropEditing,
		hideHeader,
		internalArticles,
		withBorder,
		croppedVideoStyles: pCroppedVideoStyles,
		hotSpotCropStyles,
		isInteractive,
		onClick,
		onBackClick,
		onNextClick,
		videoContainerRef,
		isTabView,
		appliedCrop,
		updateActivityPosition,
		floId,
		floIndex,
	}: {
		appliedCrop?: CropStateType;
		isCropped?: boolean;
		isSeeking?: boolean;
		isInteractive?: boolean;
		withBorder?: boolean;
		hideHeader?: boolean;
		internalArticles?: boolean;
		videoContainerRef?: React.RefObject<HTMLDivElement>;
		croppedVideoStyles: CSSProperties & {
			scaleRatio?: number;
			translateX?: number;
			translateY?: number;
		};
		hotSpotCropStyles: CSSProperties & {
			scaleRatio?: number;
			translateX?: number;
			translateY?: number;
		};
		activity: ActivityType;
		width: number;
		height: number;
		offsetWidth: number;
		offsetHeight: number;
		isCropEditing?: boolean;
		onClick?(): void;
		onBackClick?(): void;
		onNextClick?(): void;
		isTabView: boolean;
		updateActivityPosition(pos: { left: number; top: number }): void;
		floId: string;
		floIndex: number;
	}) => {
		// const isCropped = false;
		const [show, setShow] = useState(false);
		const [isAnimating, setIsAnimating] = useState(false);
		const [curActivityPosition, setCurActivityPosition] = useState<{
			id: string;
			x: number;
			y: number;
			videoScaleY?: number;
			videoScaleX?: number;
			time: number;
		}>({ id: '', time: -1, x: 0, y: 0, videoScaleY: 0, videoScaleX: 0 });
		const [transitionDuration, setTransitionDuration] = useState(0);
		const prevActivityPosition = useRef<{
			time: number;
			id: string;
			x: number;
			y: number;
		}>();
		const [hotspotElementStyle, setHotspotElementStyle] = useState({});

		const onClickCallback = useCallback(() => {
			if (isFunction(onClick)) {
				onClick();
			}
		}, [activity, onClick]);

		const isVideoWidthLessThan650 = width > 0 && width < MAX_TABLET_VIEW_WIDTH;

		const {
			scaleRatio = 2,
			translateX = 0,
			translateY = 0,
			width: PWidth,
			height: PHeight,
			// @ts-ignore
			offsetLeft: POffsetLeft,
			// @ts-ignore
			offsetTop: POffsetTop,
		} = pCroppedVideoStyles;

		const hotSpotCropStylesTransforms = omit(hotSpotCropStyles || {}, [
			'scaleRatio',
			'translateX',
			'translateY',
			'width',
			'height',
			'offsetLeft',
			'offsetTop',
		]);
		const [defaultPlacement, setDefaultPlacement] = useState('');
		const [popperAnimationName, setPopperAnimationName] = useState('');
		const hotspotRef = useRef<HTMLDivElement>(null);
		const [popperModifiers, setPopperModifiers] = useState<PopperProps['modifiers']>([]);
		const hotspotParentRef = useRef<HTMLDivElement>(null);
		const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
		const [poperRef, setPoperRef] = useState<PopperProps['anchorEl']>(null);
		const [boundaryRef, setBoundaryRef] = useState<HTMLElement | null>(null);
		const inFullScreen = useSelector((state: RootState) =>
			get(state, `floPage.flos.${floIndex}.inFullScreen`, false)
		) as boolean;

		let left = offsetWidth;
		let top = offsetHeight;
		if (isCropped && !isCropEditing) {
			left = 0;
			top = 0;
		}

		useEffect(() => {
			setPopperModifiers([
				{
					name: 'arrow',
					options: {
						element: arrowRef,
					},
				},
				{
					name: 'offset',
					options: {
						offset: [0, 10],
					},
				},
			]);
		}, [arrowRef, setPopperModifiers]);

		useEffect(() => {
			if (isInteractive) {
				if (!activity.id && prevActivityPosition.current?.id) {
					prevActivityPosition.current = {
						id: '',
						time: curActivityPosition.time,
						x: curActivityPosition.x,
						y: curActivityPosition.y,
					};
					setTransitionDuration(0);
				} else if (activity.id !== curActivityPosition.id) {
					const noPrevHotspot = !!prevActivityPosition.current?.id;
					prevActivityPosition.current = {
						id: curActivityPosition.id,
						time: curActivityPosition.time,
						x: curActivityPosition.x,
						y: curActivityPosition.y,
					};
					setIsAnimating(noPrevHotspot);
					if (noPrevHotspot) {
						setTransitionDuration(0);
					}
				}
			}
		}, [
			activity,
			setIsAnimating,
			setTransitionDuration,
			isInteractive,
			prevActivityPosition,
			curActivityPosition,
		]);

		useEffect(() => {
			const elementWidth = (hotspotParentRef?.current?.offsetWidth || 0) - 0;
			const elementHeight = (hotspotParentRef?.current?.offsetHeight || 0) - 0;
			const offsetX = min([max([curActivityPosition.x - 24, 0]), elementWidth]) as number;
			const offsetY = min([
				max([curActivityPosition.y - 24, 0]),
				elementHeight,
			]) as number;

			const transition = isInteractive
				? `left ${transitionDuration}ms ease, top ${transitionDuration}ms ease`
				: '';
			const newStyle = {
				left: offsetX,
				top: offsetY,
				transition,
			};
			setHotspotElementStyle(newStyle);
			updateActivityPosition(newStyle);
		}, [
			setHotspotElementStyle,
			updateActivityPosition,
			height,
			width,
			curActivityPosition,
			hotspotParentRef,
			transitionDuration,
			isInteractive,
		]);

		useEffect(() => {
			setShow(false);
			const croppedHeight = isCropped
				? Math.abs(get(appliedCrop, 'values.y2', 0) - get(appliedCrop, 'values.y', 0))
				: get(activity, 'windowSize.height', 0);

			const croppedWidth = isCropped
				? Math.abs(get(appliedCrop, 'values.x2', 0) - get(appliedCrop, 'values.x', 0))
				: get(activity, 'windowSize.width', 0);

			const croppedScaleW = scaleLinear(
				[0, get(appliedCrop, 'values.windowSize.width', 0)],
				[0, get(activity, 'windowSize.width', 0)]
			);
			const croppedScaleH = scaleLinear(
				[0, get(appliedCrop, 'values.windowSize.height', 0)],
				[0, get(activity, 'windowSize.height', 0)]
			);

			const pointerX = scaleLinear(
				isCropped
					? [
							croppedScaleW(get(appliedCrop, 'values.x', 0)),
							croppedScaleW(get(appliedCrop, 'values.x', 0) + croppedWidth),
					  ]
					: [0, get(activity, 'windowSize.width') || width],
				[0, width]
			)(get(activity, 'pointerPosition.x', 0));

			const pointerY = scaleLinear(
				isCropped
					? [
							croppedScaleH(get(appliedCrop, 'values.y', 0)),
							croppedScaleH(get(appliedCrop, 'values.y', 0) + croppedHeight),
					  ]
					: [0, get(activity, 'windowSize.height') || height],
				[0, height]
			)(get(activity, 'pointerPosition.y', 0));

			const newPosition = {
				x: pointerX,
				y: pointerY,
				time: get(activity, 'time', -1),
				id: activity.id,
			};

			if (isInteractive && hotspotRef.current) {
				setShow(true);
				const prevId = get(prevActivityPosition, 'current.id', '');
				if (prevId && newPosition.id !== prevId && newPosition.id) {
					const prevX = get(prevActivityPosition, 'current.x', 0) as number;
					const prevY = get(prevActivityPosition, 'current.y', 0) as number;
					const isDifferentTime =
						get(prevActivityPosition, 'current.time', -1) !== newPosition.time;
					const xDiff = newPosition.x - prevX;
					const yDiff = newPosition.y - prevY;
					const transitionTime = Math.sqrt(xDiff ** 2 + yDiff ** 2) || 1;
					setTransitionDuration(
						isDifferentTime || isSeeking
							? 0
							: (max([20000 / transitionTime, 500]) as number)
					);
					if (isDifferentTime || isSeeking) {
						setIsAnimating(false);
					}
				}
			}
			setCurActivityPosition(newPosition);
			updateActivityPosition({ left: newPosition.x - 30, top: newPosition.y - 30 });
			if (!curActivityPosition.id && !get(prevActivityPosition, 'current.id', '')) {
				prevActivityPosition.current = {
					id: newPosition.id,
					time: newPosition.time,
					x: newPosition.x,
					y: newPosition.y,
				};
			}
		}, [
			isSeeking,
			setIsAnimating,
			hotspotRef.current,
			setShow,
			isInteractive,
			appliedCrop,
			activity.id,
			height,
			offsetHeight,
			hotSpotCropStylesTransforms?.scaleRatio,
			isInteractive,
			width,
			offsetWidth,
			setIsAnimating,
			setCurActivityPosition,
			setTransitionDuration,
			prevActivityPosition,
		]);

		useEffect(() => {
			let timeout: NodeJS.Timeout;
			if (isAnimating) {
				timeout = setTimeout(() => {
					setIsAnimating(false);
				}, 1000);
			}
			return () => {
				if (timeout) {
					clearTimeout(timeout);
				}
			};
		}, [isAnimating]);

		useEffect(() => {
			// @ts-ignore
			if (activity.position || !activity) {
				setDefaultPlacement('');
				setPopperAnimationName(getPopperAnimationName(activity.position || ''));
				return;
			}
			const canvasCoordinates = get(activity, 'pointerPosition', { x: 0, y: 0 });
			const windowCoordinates = get(activity, 'windowSize', { width: 0, height: 0 });
			const defaultPlacement = findTooltipDefaultPosition(
				canvasCoordinates,
				windowCoordinates
			);
			setDefaultPlacement(defaultPlacement);
			setPopperAnimationName(getPopperAnimationName(defaultPlacement));
			// @ts-ignore
		}, [activity.id, activity, setDefaultPlacement, setPopperAnimationName]);

		const onTransitionEnd = useCallback(() => {
			const timeout = setTimeout(() => {
				setIsAnimating(false);
			}, 200);
			return () => {
				clearTimeout(timeout);
			};
		}, [setIsAnimating]);
		if (!activity.hasActivity || !curActivityPosition?.id) {
			return <></>;
		}

		const windowDimensions = getWindowDimensions(floIndex);

		const interactiveBorderDimensions =
			windowDimensions.width < MAX_MOBILE_VIEW_WIDTH ||
			(windowDimensions.height < MAX_MOBILE_VIEW_HEIGHT_IN_LANDSCAPE &&
				windowDimensions.height < windowDimensions.width)
				? MOBILE_VIEW_CLICK_ACTIVITY_BORDER_SIZE
				: CLICK_ACTIVITY_BORDER_SIZE;
		let offHeight = (windowDimensions.height - height) * 0.5;

		if (isTabView && withBorder) {
			offHeight = top - (isInteractive ? 0 : 8);
		} else if (isTabView) {
			offHeight -= isInteractive ? 0 : 8;
		}
		offHeight = max([offHeight, 0]) as number;

		return (
			<div
				className={classNames(styles.uiActivityWrapper, {
					[styles.hasHeader]: !hideHeader || internalArticles,
					[styles.withBorder]: withBorder,
					[styles.uiActivityInteractiveMode]: isInteractive,
					[styles.uiActivityInteractiveModeFullScreen]:
						isInteractive && (inFullScreen || hideHeader),
					[styles.isDev]: process.env.NODE_ENV === 'development',
				})}
				style={{
					opacity: isSeeking ? 0 : 1,
					top: withBorder && !inFullScreen && !isTabView ? undefined : offHeight,
					height: height,
					width: width,
				}}
			>
				{process.env.NODE_ENV === 'development' && (
					<div
						style={{
							left: curActivityPosition.x,
							top: curActivityPosition.y,
							position: 'absolute',
							width: 2,
							height: 2,
							borderRadius: '50%',
							border: '1px solid white',
							boxShadow: `#ff9595 0px 0px 4px 3px`,
						}}
					></div>
				)}
				<div
					className={styles.uiActivityWrapper}
					style={{
						// ...hotSpotCropStylesTransforms,
						height: height,
						width: width,
						position: 'relative',
					}}
				>
					{process.env.NODE_ENV === 'development' && (
						<div
							style={{
								left: curActivityPosition.x,
								top: curActivityPosition.y,
								position: 'absolute',
								width: 2,
								height: 2,
								borderRadius: '50%',
								border: '1px solid white',
								boxShadow: `green 0px 0px 4px 3px`,
							}}
						></div>
					)}
					<div
						className={classNames(styles.uiActivityContainer, {
							[styles.withBorder]: withBorder,
						})}
						style={{
							height: '100%',
							width: '100%',
							top: '0',
							left: '0',
						}}
						ref={hotspotParentRef}
					>
						{isInteractive && (
							<div
								ref={hotspotRef}
								style={hotspotElementStyle}
								onTransitionEnd={onTransitionEnd}
								className={styles.uiActivityInteractiveClick}
								onClick={onClickCallback}
								onTouchEnd={() => {
									console.log('on touch end');
								}}
							>
								{isAnimating ? (
									<div className={styles.movingHotspot}></div>
								) : (
									<Player
										autoplay
										loop
										speed={1}
										src={mouseAnimation}
										style={{ height: '48px', width: '48px' }}
										className={styles.infinity}
									/>
								)}
							</div>
						)}
						{isInteractive && (
							<div
								className={styles.uiActivityInteractiveBorder}
								onClick={onClickCallback}
								onTouchEnd={() => {
									console.log('on touch end');
								}}
								style={{
									width: `${interactiveBorderDimensions}px`,
									height: `${interactiveBorderDimensions}px`,
									top: curActivityPosition.y,
									left: curActivityPosition.x,
								}}
							></div>
						)}
						{!isInteractive && (
							<div
								className={styles.uiActivityMouseClick}
								style={{
									top: curActivityPosition.y - 30,
									left: curActivityPosition.x - 30,
								}}
							>
								<Player
									autoplay
									loop
									speed={2}
									src={mouseAnimation}
									style={{ height: '60px', width: '60px' }}
									className={styles.infinity}
								/>
							</div>
						)}
						{isInteractive &&
							!isSeeking &&
							hotspotRef.current &&
							show &&
							curActivityPosition.id === activity.id &&
							activity.hotspotType !== 'only_hotspot' &&
							(get(activity, 'values.title') ||
								get(activity, 'values.name') ||
								get(activity, 'values.description')) &&
							!isAnimating &&
							!isVideoWidthLessThan650 && (
								<Popper
									key={`${activity.id}_pos_${
										defaultPlacement || activity.position || 'auto'
									}`}
									ref={setPoperRef}
									className={classNames(styles.popOver)}
									open={Boolean(hotspotRef.current)}
									anchorEl={hotspotRef.current}
									// @ts-ignore
									placement={defaultPlacement || activity.position || 'auto'}
									keepMounted
									container={videoContainerRef?.current}
									modifiers={popperModifiers}
									transition
								>
									{({ TransitionProps }) => (
										<Fade {...TransitionProps} timeout={50}>
											<div
												data-position={defaultPlacement || activity.position}
												style={{
													width: '100%',
													height: '100%',
													position: 'relative',
												}}
												className={classNames(styles.animation)}
											>
												<span className={styles.arrow} ref={setArrowRef} />
												<Box
													className={styles.box}
													minWidth={100}
													maxWidth={280}
													key={activity.id}
												>
													{(activity.hotspotType
														? includes(activity.hotspotType, 'title')
														: get(activity, 'values.title') ||
														  get(activity, 'values.name')) && (
														<div
															className={classNames(styles.contentEditable, styles.title)}
															dangerouslySetInnerHTML={{
																__html: sanitizeHtml(
																	get(activity, 'values.title') ||
																		get(activity, 'values.name') ||
																		''
																),
															}}
														></div>
													)}
													{(activity.hotspotType
														? includes(activity.hotspotType, 'desc')
														: get(activity, 'values.description')) && (
														<div
															className={classNames(
																styles.contentEditable,
																styles.description,
																'instruction-description'
															)}
															dangerouslySetInnerHTML={{
																__html: sanitizeHtmlLinks(
																	get(activity, 'values.description') || ''
																),
															}}
														></div>
													)}
												</Box>
											</div>
										</Fade>
									)}
								</Popper>
							)}
					</div>
				</div>
			</div>
		);
	}
);

let timeout: NodeJS.Timeout | undefined = undefined;
//@ts-ignore
const VideoPlayer = ({
	videoOptions,
	hasAudioTrack,
	isAiVoice,
	screenShareOptions,
	audioOptions,
	setPlayerRef,
	setVideoReady,
	className,
	disableAnnotations,
	setSeekTime,
	seekTimeCount,
	selectedCommentId,
	setError,
	hasError,
	boundToContainer = false,
	enableAreaSelection = false,
	areaSelected,
	onAreaSelected,
	duration: defaultDuration,
	customControlsChildren,
	onSetPlayerDim,
	setVideoSize,
	floName,
	createdByName,
	createdOn,
	appliedCrop,
	internalArticles,
	hideHeader,
	withBorder,
	videoSize,
	isTabView,
	floBodyRef,
	hidePlayer,
	elementVoiceResources,
	elementsVoiceEnabled,
	backgroundMusicResource,
	floId,
	floIndex,
}: {
	appliedCrop?: CropStateType;
	videoOptions: videojs.PlayerOptions;
	screenShareOptions: videojs.PlayerOptions;
	audioOptions: videojs.PlayerOptions;
	setPlayerRef(ref: HTMLVideoElement | null): void;
	className?: string;
	isAiVoice?: boolean;
	hasAudioTrack?: boolean;
	withBorder?: boolean;
	floName?: string;
	createdByName?: string;
	createdOn?: string;
	videoSize: { height: number; width: number } | undefined | null;
	setVideoSize(value: { height: number; width: number } | undefined | null): void;
	setError(val: boolean): void;
	setVideoReady?(val: boolean): void;
	disableAnnotations?: boolean;
	enableAreaSelection?: boolean;
	trimRange?: number[];
	minTrimTime?: number;
	setSeekTime?: Function;
	seekTimeCount?: number;
	duration?: number;
	hasError?: boolean;
	internalArticles?: boolean;
	hideHeader?: boolean;
	selectedCommentId?: string;
	setSelectedCommentId: Function;
	createdBy?: Partial<Profile>;
	boundToContainer?: boolean;
	areaSelected?: CropAreaType;
	onSetPlayerDim?(data: {
		width: number;
		height: number;
		offsetWidth: number;
		offsetHeight: number;
	}): void;
	onAreaSelected?(cropData: CropAreaType): void;
	customControlsChildren?: ReactElement;
	isTabView: boolean;
	floBodyRef: MutableRefObject<HTMLDivElement>;
	hidePlayer: boolean;
	elementVoiceResources:
		| { [key: string]: { voiceId: string; scriptId: string } }
		| undefined;
	backgroundMusicResource: BackgroundMusicResourceType | undefined;
	elementsVoiceEnabled: boolean;
	floId: string;
	floIndex: number;
}) => {
	const floatingRef = useRef<undefined | HTMLDivElement>();
	const screenShareControls = useRef<videojs.Player>(null);
	const videoControls = useRef<videojs.Player>(null);
	const audioControls = useRef<videojs.Player>(null);
	const [isHoveringOnProgress, setProgressHoverState] = useState(false);
	const [presenterVideoOptions, setPresenterVideoOptions] = useState(
		DEFAULT_PRESENTER_OPTIONS
	);
	const [videoReady, setReady] = useState(false);
	const [primaryVideoReady, setPrimaaryVideoReady] = useState(false);
	const [presenterVideoReady, setPresenterVideoReady] = useState(false);
	const [isAnnotationProgress, setAnnotationProgress] = useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const screenShareNode = React.useRef<HTMLVideoElement>(null);
	const videoNode = React.useRef<HTMLVideoElement>(null);
	const audioNode = React.useRef<HTMLVideoElement>(null);
	const secondaryVideoRef = React.useRef<HTMLDivElement>(null);
	const [playerDim, setPlayerDim] = useState({
		width: 0,
		height: 0,
		offsetWidth: 0,
		offsetHeight: 0,
	});
	const microApp = isMicroApp();
	const isVideoWidthLessThan650 =
		playerDim.width > 0 && playerDim.width < MAX_TABLET_VIEW_WIDTH;
	const setPlayerDimCallback = useCallback(
		(data: {
			width: number;
			height: number;
			offsetWidth: number;
			offsetHeight: number;
		}) => {
			if (
				playerDim.width !== data.width ||
				playerDim.height !== data.height ||
				playerDim.offsetWidth !== data.offsetWidth ||
				playerDim.offsetHeight !== data.offsetHeight
			) {
				setPlayerDim(data);
				if (isFunction(onSetPlayerDim)) onSetPlayerDim(data);
			}
		},
		[setPlayerDim, playerDim]
	);

	const wrapper = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['wrapper']`, 'rounded')
	);
	const embeddedElements = document.querySelectorAll('floik-flo');
	const floikFloElement = embeddedElements[floIndex];

	const showWrapper = wrapper && wrapper !== 'rounded';
	const skipInactivity = isSwiftPlay(floIndex);
	const [videoPlayerDim, setVideoPlayerDim] = useState({
		initial: true,
		width: 0,
		height: 0,
	});
	const [seekTime, setLocalSeekTime] = useState(0);
	const windowDimensions = getWindowDimensions(floIndex);

	const [bounds, setBounds] = useState({
		top: 0,
		left: 0,
		right: windowDimensions.width,
		bottom: windowDimensions.height,
	});

	const [activity, setActivity] = useState<{
		id: string;
		text: string;
		hasActivity: boolean;
		pointerPosition: {
			x: number;
			y: number;
		};
		elementBounds: {
			x: number;
			y: number;
			width: number;
			height: number;
		};
		windowSize: {
			width: number;
			height: number;
		};
	}>({
		id: '',
		text: '',
		hasActivity: false,
		pointerPosition: {
			x: 0,
			y: 0,
		},
		elementBounds: {
			x: 0,
			y: 0,
			width: 0,
			height: 0,
		},
		windowSize: {
			width: 0,
			height: 0,
		},
	});
	const [videoPlayerTimeout, setVideoPlayerTimeout] = useState<number>(-1);
	const [currentElementTime, setCurrentElementTime] = useState(-1);
	const [gotoElementIndex, setGotoElementIndex] = useState<number>(-1);
	const [lastElementIndex, setlastElementIndex] = useState<number>(0);
	const [currentElementIndex, setCurrentElementIndex] = useState<number>(-1);
	const [currentElement, setCurrentElement] = useState<
		publishElementsDataType | undefined | null
	>();

	const [activityPosition, setActivityPosition] = useState({ left: 0, top: 0 });
	const [isSeeking, setIsSeeking] = useState(false);
	const [imageCountDownPause, setImageCountDownPause] = useState(true);
	const [leftSeekPosition, setLeftSeekPosition] = useState(0);
	const [volume, setVolume] = useState(1);
	const [leftPosition, setLeftPosition] = useState(0);
	const [bottomPosition, setBottomPosition] = useState(0);
	const [isArrowClick, setIsArrowClick] = useState(false);
	const hasNotPlayed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.hasNotPlayed`, true)
	);
	const appComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.appCommentPosition`)
	);
	const showAppComment = get(appComment, 'show');
	const brandImage = useSelector(headerBrandImageSelector(floIndex));
	const isFloClosed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.closed`)
	);
	const videoResourceProperties = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.videoResourceProperties`)
	);
	const [currentAnnotations, setCurrentAnnotations] = useState<AnnotationMetaSnapshot>();
	const annotationsMeta: AnnotationMetaSnapshot[] = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.annotationsMeta`, [])
	);
	const floDuration = useSelector(floDurationSelector(floIndex)) || 0;
	const currentTimeSeconds = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.currentTimeSeconds`, 0)
	);

	const allowComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.allowComment`, false)
	);
	const isNoVoice = useSelector((state: RootState): boolean => {
		const elementsConfig = get(
			state,
			`floPage.flos.${floIndex}.editorsElementsConfig`,
			[]
		);
		const selectedElementsConfig = find(
			elementsConfig,
			(e) => get(e, 'editorElementName') === 'voice-over'
		);
		const voiceOverProperties = get(selectedElementsConfig, 'properties');
		return get(voiceOverProperties, 'voiceOverMode') === 'NO_VOICE';
	});

	const floElements: {
		[key: string]: publishElementsDataType[];
	} = useSelector((state) => get(state, `floPage.flos.${floIndex}.editorsElements`, {}));

	const [hotspot, setHotspot] = useState<{
		count: number;
		current: HotSpotType;
	}>({
		count: 0,
		current: {
			id: '',
			time: -1,
			text: '',
			order: 0,
			enableZoom: false,
			hasActivity: false,
			position: '',
			pointerPosition: {
				x: 0,
				y: 0,
			},
			elementBounds: {
				x: 0,
				y: 0,
				width: 0,
				height: 0,
			},
			windowSize: {
				width: 0,
				height: 0,
			},
		},
	});
	const hotspotsTimes = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.hotspotTimes`)
	);
	const hidePresenterVideo = useSelector((state) => {
		const elem = find(get(state, `floPage.flos.${floIndex}.editorsElementsConfig`), [
			'editorElementName',
			'property-presenter',
		]);
		return get(elem, 'properties.hideElement');
	});
	const isEmbed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["is-embed"]`, false)
	);
	const [imageElement, setImageElement] = useState<
		publishElementsDataType | undefined | null
	>(undefined);

	const [textElement, setTextElement] = useState<
		publishElementsDataType | undefined | null
	>(undefined);

	const ctaAndHotspotTimes: undefined | stepsElementType[] = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.ctaAndHotspotTimesList`)
	);
	const hotspotsGroups: {
		[key: string]: elementsDataType[];
	} = useSelector((state) => get(state, `floPage.flos.${floIndex}.hotspotGroups`, {}));

	const autoPlay = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["auto-play"]`)
	);
	const timerRef = useRef<NodeJS.Timeout | undefined>();
	const [showingElement, setShowingElement] = useState(false);

	// const prevHotspotTime = useRef<number | undefined>(-1);
	// @ts-ignore
	const prevHotspotRef = useRef<{
		x: number;
		y: number;
		x2: number;
		y2: number;
		windowSize: {
			width: number;
			height: number;
		} | null;
	}>(null);
	const onPlayRef = useRef<((from?: string) => void) | null>(null);
	const amplitudeData: any = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.amplitudeData`, [])
	);
	const dialogId: any = useSelector((state) => get(state, `videoDialog.${floIndex}.id`));
	const ctaId: any = useSelector((state) => get(state, `videoDialog.${floIndex}.ctaId`));
	const dialogOnStartConfirm = useSelector((state) =>
		get(state, `videoDialog.${floIndex}.onConfirm`)
	);
	const annotations = useSelector((state: RootState) => {
		const elems = flatten(
			map(
				get(state, `floPage.flos.${floIndex}.annotations`, []),
				// @ts-ignore
				(i) => i.state
			)
		);
		return sortBy(
			elems,
			(e) => Math.abs(get(e, 'width', 0)) * Math.abs(get(e, 'height', 0))
		);
	});
	const currentUser = useSelector(currentProfileSelector);
	const dispatch = useDispatch();
	const floSpaceId = useSelector(floSpaceIdSelector(floIndex));
	const active = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.annotationTool`, 'none')
	);
	const videoContainerRef = useRef<HTMLDivElement>(null);
	const tool = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.annotationTool`, 'none')
	);
	const actualDuration =
		get(screenShareNode, 'current.duration', 0) || get(videoNode, 'current.duration', 0);
	const duration =
		isFinite(actualDuration) && actualDuration ? actualDuration : defaultDuration;

	const [isPause, setPause] = useState(true);
	const [enableAddComment, setEnableAddComment] = useState({
		visible: false,
		top: 0,
		left: 0,
		question: '',
		name: '',
		email: '',
		className: '',
	});
	const [scrollTopPosition, setScrollTopPosition] = useState(0);
	const skipVideo = useSelector(
		(state) =>
			get(state, `floPage.flos.${floIndex}.floDetails["interactive-mode"]`) &&
			get(state, `floPage.flos.${floIndex}.floDetails["skip-video"]`)
	);
	const { sendCompletionStatus, resetLastSeekedTime } = useTrackCompletionStatus({
		duration: duration || 0,
		floId,
		floIndex,
	});

	const subtitlesList =
		useSelector((state) => get(state, `floPage.flos.${floIndex}.subtitlesList`)) || [];

	const onScrollHandler = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			// @ts-ignore
			setScrollTopPosition(event.target?.scrollTop);
		},
		[setScrollTopPosition]
	);

	useEffect(() => {
		const debouncedScrollHandler = debounce(onScrollHandler, 50);
		// @ts-ignore
		floBodyRef?.current?.addEventListener('scroll', debouncedScrollHandler);
		return () => {
			// @ts-ignore
			floBodyRef?.current?.removeEventListener('scroll', debouncedScrollHandler);
		};
	}, [onScrollHandler, floBodyRef?.current]);

	const validEmail = get(enableAddComment, 'email', '').trim()
		? /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
				get(enableAddComment, 'email', '').trim()
		  )
		: true;

	const prevTool = useRef({ tool }).current;
	const prevClickId = useRef<string>(get(activity, 'id', ''));
	const interactiveMode = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["interactive-mode"]`)
	);
	const hasScreen = !!get(screenShareOptions, 'sources[0].src');
	const mainPlayerOption = hasScreen ? screenShareOptions : videoOptions;
	const secondaryPlayerOption = hasScreen ? videoOptions : screenShareOptions;
	const isAudioWaveAvailable = !isEmpty(amplitudeData);

	const setScreenShareControls = useCallback(
		(playerRef: videojs.Player) => {
			// @ts-ignore
			screenShareControls.current = playerRef;
		},
		[screenShareControls]
	);
	const setVideoControls = useCallback(
		(playerRef: videojs.Player) => {
			// @ts-ignore
			videoControls.current = playerRef;
		},
		[videoControls]
	);
	const setAudioControls = useCallback(
		(playerRef: videojs.Player) => {
			// @ts-ignore
			audioControls.current = playerRef;
		},
		[audioControls]
	);
	useEffect(() => {
		const aspectRatio = calculateAspectRatio(
			get(videoResourceProperties, 'width', 0),
			get(videoResourceProperties, 'height', 1)
		);
		if (videoReady && aspectRatio) {
			setVideoSize({
				width: get(videoResourceProperties, 'width', 0),
				height: get(videoResourceProperties, 'height', 0),
			});
		} else {
			setVideoSize(null);
		}
	}, [videoReady, videoResourceProperties, setVideoSize]);

	const resetHotspot = useCallback(() => {
		setHotspot({
			count: 0,
			current: {
				id: '',
				time: -1,
				text: '',
				order: 0,
				enableZoom: false,
				hasPrev: true,
				hasActivity: false,
				position: '',
				pointerPosition: {
					x: 0,
					y: 0,
				},
				elementBounds: {
					x: 0,
					y: 0,
					width: 0,
					height: 0,
				},
				windowSize: {
					width: 0,
					height: 0,
				},
			},
		});
	}, [setHotspot]);

	const currentTime = screenShareControls.current?.currentTime() || 0;

	const { pauseTimeList, interactionsList } = useMemo(() => {
		let curPause: pauseTimeType;
		const newPauses: pauseTimeType[] = [];
		let curIndex = 0;

		const interactionsList = map(ctaAndHotspotTimes || [], (item: { time: number }) => {
			const isStayForImage = get(item, 'meta.behaviourType') === 'stayFor';
			return {
				start: item.time,
				isInteraction: !isStayForImage,
				end: item.time,
			};
		});
		const timesList = sortBy(
			interactionsList.concat(get(backgroundMusicResource, 'textPauses', [])),
			(item) => item.start
		);

		while (curIndex < get(timesList, 'length', 0)) {
			const item = get(timesList, `[${curIndex}]`);
			curPause = { ...(item || {}) } as pauseTimeType;
			if (
				curIndex !== get(timesList, 'length', 0) - 1 &&
				get(timesList, `[${curIndex + 1}].start`, 0) < get(curPause, 'end', 0) + 4
			) {
				while (
					curIndex + 1 < get(timesList, 'length', 0) &&
					get(timesList, `[${curIndex + 1}].start`, 0) < get(curPause, 'end', 0) + 4
				) {
					curPause = {
						...curPause,
						end:
							max([
								get(timesList, `[${curIndex + 1}].end`, 0),
								get(timesList, `[${curIndex}].end`, 0),
								get(curPause, 'end'),
							]) || 0,
					};
					curIndex += 1;
				}
			}
			curIndex += 1;
			newPauses.push(curPause);
		}

		return {
			pauseTimeList: newPauses,
			interactionsList,
		};
	}, [ctaAndHotspotTimes, backgroundMusicResource]);

	const {
		onToggleMuteBgCb,
		onRestartBgCb,
		onVideoPauseBgCb,
		onPlayBgCb,
		hideVoiceIcon,
		onVideoEndBgCb,
	} = useBackgroundMusic({
		url: get(backgroundMusicResource, 'properties')
			? get(backgroundMusicResource, 'url', '')
			: '',
		pauseTimeList,
		interactionsList,
		isMuted: volume === 0,
		currentTime,
		pauseOnInteractions:
			get(backgroundMusicResource, 'properties.pauseOnInteraction', false) && !autoPlay,
		duration: duration || 0,
		fadeInOut: get(backgroundMusicResource, 'properties.fadeInFadeOut', true),
		volume: get(backgroundMusicResource, 'properties.volumeLevel', 0.8),
		videoEnded:
			dialogId === 'end' || (currentTime > 0 && currentTime >= (duration || 0) - 0.1),
	});

	const onPause = useCallback(
		(value?: { manual?: boolean }) => {
			// console.trace('on pause ');
			// if (autoPlay && currentElementIndex >= 0) return;
			if (value?.manual) {
				onVideoPauseBgCb();
			}
			if (screenShareControls.current && !screenShareControls.current.paused()) {
				screenShareControls.current.pause();
			}
			if (videoControls.current && !videoControls.current.paused())
				videoControls.current.pause();
			if (audioControls.current && !audioControls.current.paused())
				audioControls.current.pause();
			if (!disableAnnotations)
				dispatch(setAnnotationToolAction({ data: 'rect', floId, floIndex }));
		},
		[
			autoPlay,
			currentElementIndex,
			disableAnnotations,
			screenShareControls.current,
			videoControls.current,
			audioControls.current,
			onVideoPauseBgCb,
		]
	);

	useEffect(() => {
		if (Number.isInteger(seekTimeCount) && screenShareControls) {
			// @ts-ignore
			setSeekTime(screenShareControls.current.currentTime());
		}
	}, [seekTimeCount, screenShareControls, setSeekTime]);

	useEffect(() => {
		if (prevTool.tool !== tool) {
			if (screenShareControls.current) {
				if (tool !== 'none') {
					if (!screenShareControls.current.paused())
						// @ts-ignore
						screenShareControls.current.pause();
					onTimeUpdate();
				}
			}
		}
		return () => {
			prevTool.tool = tool;
		};
	}, [screenShareControls, prevTool, tool, isPause]);

	const getContainer = useCallback(() => {
		const doc = document.querySelector('.screenShareTrack');
		return doc || containerRef.current;
	}, [containerRef.current]);

	useEffect(() => {
		if (dialogId === 'start' && playerDim.height) {
			dispatch(
				updateDialogAction({
					data: {
						fullScreenView: playerDim.height < 200,
					},
					floId,
					floIndex,
				})
			);
		}
	}, [playerDim?.height, dialogId]);

	const showEndCta = useCallback(
		(hideHotspot?: boolean) => {
			if (hideHotspot) {
				resetHotspot();
			}
			const ctaElemEnd = get(floElements, 'info_ctaend')?.[0];
			screenShareControls?.current?.pause();
			screenShareControls.current?.currentTime((duration || 0) - 0.05);
			sendCompletionStatus(duration || 0);
			const onConfirm = (_dispatch?: Dispatch, clicked?: boolean) => {
				onRestartBgCb();
				if (clicked) {
					dispatch(setAutoPlay({ data: false, floId, floIndex }));
				}
				if (screenShareControls.current) {
					screenShareControls.current.currentTime(duration || 0);
					screenShareControls.current.currentTime(0);
				}
				(ctaAndHotspotTimes || []).forEach((cta) => {
					dispatch(
						skipCta({
							id: get(cta, 'id', ''),
							enable: true,
							floId,
							floIndex,
						})
					);
				});
				setGotoElementIndex(-1);
				setCurrentElementIndex(-1);
				setCurrentElementTime(-1);
				setCurrentElement(null);

				dispatch(trackViewCountAction({ restart: true, floId, floIndex }));
				// prevHotspotTime.current = -1;
				dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
				dispatch(
					hideDialogAction({
						data: 'end',
						floId,
						floIndex,
					})
				);
				setIsArrowClick(false);
				return true;
			};
			setShowingElement(true);
			dispatch(
				showDialogAction({
					data: {
						id: 'end',
						position: !isVideoWidthLessThan650 ? get(ctaElemEnd, 'positionOnScreen') : '',
						visible: !(get(ctaElemEnd, 'linkedImageId') && isVideoWidthLessThan650),
						ctaId: get(ctaElemEnd, 'id'),
						disableAutoFocus: isEmbed,
						disablePortal: true,
						fullScreenView: playerDim.height < 200,
						container: isVideoWidthLessThan650 ? floikFloElement : getContainer,
						classDialogRoot: classNames(styles.classDialogRoot, {
							[styles.interactiveDemoDialogRoot]: interactiveMode,
							[styles.backdropTransparent]: get(ctaElemEnd, 'linkedImageId'),
						}),
						headerClassName: styles.headerClassName,
						noBackdropClick: true,
						bodyClassName: styles.bodyClassName,
						classDialogConfirmButton: styles.classDialogConfirmButton,
						header: get(ctaElemEnd, 'values.heading', ''),
						centerAlignFooter: true,
						confirmButtonText: get(ctaElemEnd, 'values.button.name', ''),
						confirmButtonUrl: get(ctaElemEnd, 'values.button.url', ''),
						...getAdventureCtaDialogProperties(ctaElemEnd, floId, floIndex),
						onConfirm,
						onCancel: onConfirm,
					},
					floId,
					floIndex,
				})
			);
		},
		[
			setShowingElement,
			ctaAndHotspotTimes,
			setGotoElementIndex,
			setCurrentElementIndex,
			setCurrentElementTime,
			floElements,
			getContainer,
			screenShareControls.current,
			resetHotspot,
			setIsArrowClick,
			playerDim,
			duration,
			isVideoWidthLessThan650,
			onRestartBgCb,
			floikFloElement,
		]
	);

	// @ts-ignore
	const goToStep = useCallback(
		(currentElementId?: string, next?: boolean, arrowClick?: boolean) => {
			setTextElement(undefined);
			setImageElement(undefined);
			setIsArrowClick(!!arrowClick);
			let index = currentElementIndex;
			if (currentElementIndex === -1) {
				index = lastElementIndex;
			}
			let elements = ctaAndHotspotTimes;
			const newStepIndex = next ? index + 1 : index - 1;

			// @ts-ignore
			const newElement: stepsElementType = get(elements, newStepIndex);
			// @ts-ignore
			const currentElement: stepsElementType = get(elements, index);
			if (includes(['cta', 'info_ctastart', 'info_cta_end'], get(newElement, 'type'))) {
				dispatch(skipCta({ id: get(newElement, 'id'), enable: true, floId, floIndex }));
			}
			if (
				!skipVideo &&
				!arrowClick &&
				next &&
				(get(newElement, 'time', -1) !== get(currentElement, 'time', 0) ||
					get(newElement, 'disable'))
			) {
				if (onPlayRef?.current) {
					onPlayRef.current();
				}
			} else {
				// @ts-ignore
				console.log('go to step new', newStepIndex);
				setGotoElementIndex(newStepIndex);
			}
			return get(newElement, 'time') !== get(currentElement, 'time');
		},
		[
			onPlayRef,
			setCurrentElement,
			setCurrentElementIndex,
			setGotoElementIndex,
			currentElementIndex,
			lastElementIndex,
			// ctaAndHotspotTimes,
			screenShareControls,
			setIsArrowClick,
		]
	);

	// @ts-ignore
	const checkAndSkipInactivity = useCallback(
		(curElementId: string) => {
			if (skipInactivity) {
				return goToStep(curElementId, true);
			}
			return true;
		},
		[goToStep]
	);

	const showHotspot = useCallback(
		(hotspotElem: elementsDataType, cb?: Function) => {
			let prevHotspot;
			let hasPrev = false;
			if (hotspot.current.order > 1) {
				// find previous element from the same group
				prevHotspot = find(hotspotElem, ['order', hotspotElem.order - 1]);
				hasPrev = !!prevHotspot;
			}
			const lastTime = findLast(
				hotspotsTimes,
				(time: number) => time < (hotspotElem.time || -1)
			);
			if (!hasPrev) {
				// find item from previous group
				const prevGroupHotspot = hotspotsGroups[lastTime || -1] || [];
				prevHotspot = last(prevGroupHotspot);
				hasPrev = !!find(hotspotElem, ['order', (prevHotspot?.order || 0) - 1]);
			}
			if (!hasPrev) {
				const prevGroup = isNumber(lastTime) && hotspotsGroups[String(lastTime)];
				if (prevGroup && prevGroup.length > 0) {
					hasPrev = true;
				}
			}
			// prevHotspotTime.current = hotspotElem.time;
			onPause();
			const currentGroupHotspots =
				hotspotsGroups[
					hotspotElem.time || hotspotElem.time === 0 ? hotspotElem.time : -1
				] || [];
			// @ts-ignore
			setHotspot((hs) =>
				get(hs, 'current.id') === get(hotspotElem, 'id')
					? hs
					: {
							count: currentGroupHotspots.length,
							current: {
								id: hotspotElem?.id || '',
								time: get(hotspotElem, 'time') as number,
								text: get(hotspotElem, 'text', ''),
								hasActivity: true,
								hasPrev,
								enableZoom: get(hotspotElem, 'enableZoom', false),
								position: get(hotspotElem, 'position', ''),

								pointerPosition: {
									x: get(hotspotElem, 'canvasRenderConfig.canvasCoordinates.x', 0),
									y: get(hotspotElem, 'canvasRenderConfig.canvasCoordinates.y', 0),
								},
								// @ts-ignore
								elementBounds: get(
									hotspotElem,
									'canvasRenderConfig.closestElementBounds'
								),
								values: get(hotspotElem, 'values'),
								order: get(hotspotElem, 'order') || 1,
								hotspotType:
									get(hotspotElem, 'subElementName') || get(hotspotElem, 'subElement'),
								// @ts-ignore
								windowSize: get(hotspotElem, 'canvasRenderConfig.windowCoordinates'),
								cb,
							},
					  }
			);
			if (hotspotElem?.id && get(hotspotElem, 'time', -1) >= 0) {
				if (screenShareControls?.current) {
					screenShareControls.current.currentTime(
						get(hotspotElem, 'nonRoundedTime') ?? get(hotspotElem, 'time', 0)
					);
				}
			}
		},
		[screenShareControls, hotspot, hotspotsTimes, hotspotsGroups, onPause, setHotspot]
	);

	const sendAnnotationComment = useCallback(() => {
		dispatch(
			sendAnnotationAction({
				data: last(annotationsMeta),
				question: enableAddComment.question,
				name: enableAddComment.name,
				email: enableAddComment.email,
				floIndex,
				floId,
			})
		);

		if (get(enableAddComment, 'name', ''))
			Cookie.set('floik-username', get(enableAddComment, 'name', ''), {
				path: '/',
			});
		if (get(enableAddComment, 'email', ''))
			Cookie.set('floik-user-email', get(enableAddComment, 'email', ''), {
				path: '/',
			});

		setEnableAddComment({
			visible: false,
			top: 0,
			left: 0,
			question: '',
			name: '',
			email: '',
			className: '',
		});
		dispatch(setAnnotationToolAction({ data: 'none', floId, floIndex }));
		dispatch(setAllowComment({ data: false, floId, floIndex }));
	}, [enableAddComment, mainPlayerOption, floId, floSpaceId, floId]);

	const onChangeHandler = useCallback((event: React.ChangeEvent, name: string) => {
		setEnableAddComment((data) => ({
			...data,
			[name]: get(event, 'target.value'),
			className: '',
		}));
	}, []);

	const onQuestionChange = useCallback(
		(e: React.ChangeEvent) => {
			onChangeHandler(e, 'question');
		},
		[onChangeHandler]
	);

	const onEmailChange = useCallback(
		(e: React.ChangeEvent) => {
			onChangeHandler(e, 'email');
		},
		[onChangeHandler]
	);

	const onNameChange = useCallback(
		(e: React.ChangeEvent) => {
			onChangeHandler(e, 'name');
		},
		[onChangeHandler]
	);

	const onKeyDownCallback = useCallback(
		(ev: React.KeyboardEvent<HTMLTextAreaElement>, name: string) => {
			if (ev.key === 'Escape') {
				if (!enableAddComment.question) {
					setEnableAddComment({
						visible: false,
						top: 0,
						left: 0,
						question: '',
						name: '',
						email: '',
						className: '',
					});
					dispatch(updateAnnotationAction({ floId, floIndex }));
					dispatch(setAllowComment({ data: false, floId, floIndex }));
				} else if (floatingRef.current) {
					setEnableAddComment((o) => ({ ...o, className: styles.wiggle }));
					if (timeout) clearTimeout(timeout);
					timeout = setTimeout(() => {
						setEnableAddComment((o) => ({ ...o, className: '' }));
					}, 2000);
				}
			}
			if (ev.key === 'Enter' && !ev.shiftKey) {
				if (
					get(enableAddComment, 'email', '').trim() &&
					!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
						get(enableAddComment, 'email', '').trim()
					)
				) {
					dispatch(
						setNotification({
							data: {
								type: 'error',
								message: 'Invalid email',
								canShow: true,
							},
							floIndex,
						})
					);
					return;
				}
				if (get(enableAddComment, 'question', '').trim()) {
					sendAnnotationComment();
				}
			}
		},
		[enableAddComment, setEnableAddComment, timeout]
	);

	const onQuestionKeyDownCallback = useCallback(
		(ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
			onKeyDownCallback(ev, 'question');
		},
		[onKeyDownCallback]
	);

	const onNameKeyDownCallback = useCallback(
		(ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
			onKeyDownCallback(ev, 'name');
		},
		[onKeyDownCallback]
	);

	const hasBgMusic = !!get(backgroundMusicResource, 'properties');

	const onEmailKeyDownCallback = useCallback(
		(ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
			onKeyDownCallback(ev, 'email');
		},
		[onKeyDownCallback]
	);

	useEffect(() => {
		const interval = setInterval(() => {
			if (
				isSeeking ||
				(dialogId && gotoElementIndex === -1) ||
				!videoReady ||
				!playerDim.width ||
				!playerDim.height
			) {
				return;
			}

			if (!screenShareControls.current) return;
			// @ts-ignore
			const currentTime =
				Math.round(screenShareControls.current.currentTime() * 100) / 100;

			const newIndex =
				gotoElementIndex >= 0
					? gotoElementIndex
					: findIndex(
							ctaAndHotspotTimes,
							(item) =>
								get(item, 'time', 0) > currentTime - 0.07 &&
								get(item, 'time', 0) <=
									currentTime + (get(item, 'retentionTimeInSec', 0) || 0.35)
					  );

			const prevElement = get(ctaAndHotspotTimes, lastElementIndex);
			if (
				prevElement &&
				((get(prevElement, `time`, 0) &&
					currentTime < get(prevElement, `time`, 0) - 0.07) ||
					currentTime >
						get(prevElement, `time`, 0) +
							(get(prevElement, `retentionTimeInSec`, 0) || 0.35)) &&
				get(prevElement, `type`) === 'cta' &&
				get(prevElement, `disable`)
			) {
				dispatch(
					skipCta({
						id: get(prevElement, `id`, ''),
						enable: true,
						floId,
						floIndex,
					})
				);
			}

			if (newIndex === currentElementIndex) {
				if (gotoElementIndex >= 0) {
					setGotoElementIndex(-1);
				}
				return;
			}
			const element = get(ctaAndHotspotTimes, newIndex);
			if (get(element, 'id') === get(currentElement, 'id', '0')) {
				return;
			}

			if (
				!get(element, 'disable') &&
				// &&
				// currentElementIndex <= 0
				(gotoElementIndex >= 0 ||
					// @ts-ignore
					get(ctaAndHotspotTimes, `[${newIndex}].time`, -1) !==
						get(ctaAndHotspotTimes, `[${currentElementIndex}].time`, -2))
			) {
				if (screenShareControls.current.seeking()) return;
				// get(floElements)
				setCurrentElementIndex(newIndex);
				if (newIndex >= 0) {
					setlastElementIndex(newIndex);
				}
				setCurrentElementTime(get(element, 'time', -1));
				setGotoElementIndex(-1);
				if (
					element &&
					get(element, 'type') !== 'clicks' &&
					Math.round(get(element, 'time', -1) * 100) / 100 !== currentTime
				) {
					screenShareControls.current.currentTime(get(element, 'time', -1));
				}
			}
		}, 100);
		return () => {
			clearInterval(interval);
		};
	}, [
		gotoElementIndex,
		isSeeking,
		ctaAndHotspotTimes,
		setlastElementIndex,
		currentElement,
		currentElementIndex,
		setCurrentElementIndex,
		setCurrentElementTime,
		dialogId,
		onPause,
		floElements,
		hasNotPlayed,
		interactiveMode,
		prevClickId,
		screenShareControls,
		setActivity,
		hotspot,
		hotspotsTimes,
		hotspotsGroups,
		checkAndSkipInactivity,
		showHotspot,
		resetHotspot,
		playerDim,
		isVideoWidthLessThan650,
		videoReady,
		duration,
		lastElementIndex,
	]);

	useEffect(() => {
		const ctaHotSpotItem = get(ctaAndHotspotTimes, currentElementIndex);
		const nextCtaHotSpotItem = get(ctaAndHotspotTimes, currentElementIndex + 1);
		const type: string = get(ctaHotSpotItem, 'type', '');
		let actualType = type;
		if (type === 'hotspot') {
			actualType = 'hotspots';
		}
		if (type === 'cta') {
			if (get(ctaHotSpotItem, 'isStartCta')) {
				actualType = 'info_ctastart';
			} else if (get(ctaHotSpotItem, 'isEndCta')) {
				actualType = 'info_ctaend';
			}
		}
		const elem = find(get(floElements, actualType), ['id', get(ctaHotSpotItem, 'id')]);
		if (get(currentElement, 'id') === get(elem, 'id')) {
			return;
		}

		if (!get(elem, 'disable')) {
			// @ts-ignore
			setCurrentElement(elem);
			let resetCtaElem = true;
			let resetHotspotElem = true;
			let resetImageElem = true;
			let resetTextElem = true;
			let resetClickElem = true;
			let pauseVideo = false;
			switch (actualType) {
				case 'image': {
					resetImageElem = false;
					pauseVideo = true;
					// @ts-ignore
					screenShareControls.current?.currentTime(get(elem, 'time'));
					break;
				}
				case 'clicks':
				case 'click': {
					resetClickElem = false;
					pauseVideo = false;
					prevClickId.current = get(elem, 'id', '');
					setShowingElement(true);
					setActivity({
						id: get(elem, 'id', ''),
						text: get(elem, 'text', ''),
						hasActivity: true,
						pointerPosition: {
							x: get(elem, 'canvasRenderConfig.canvasCoordinates.x', 0),
							y: get(elem, 'canvasRenderConfig.canvasCoordinates.y', 0),
						},
						// @ts-ignore
						elementBounds: get(elem, 'canvasRenderConfig.closestElementBounds'),
						// @ts-ignore
						windowSize: get(elem, 'canvasRenderConfig.windowCoordinates'),
					});
					break;
				}
				case 'hotspots': {
					resetHotspotElem = false;
					pauseVideo = true;
					// @ts-ignore
					screenShareControls.current?.currentTime(get(elem, 'time'));
					setShowingElement(true);
					// @ts-ignore
					showHotspot(elem);
					break;
				}
				case 'text': {
					resetTextElem = false;
					pauseVideo = true;
					// @ts-ignore
					screenShareControls.current?.currentTime(get(elem, 'time'));
					setShowingElement(true);
					setTextElement(elem);
					break;
				}
				case 'info_ctastart':
				case 'info_ctaend':
				case 'cta': {
					resetCtaElem = false;

					const ctaElemStart = get(floElements, 'info_ctastart')?.[0];
					const ctaElemEnd = get(floElements, 'info_ctaend')?.[0];
					const ctaPresentWithHotspotTime =
						nextCtaHotSpotItem &&
						// @ts-ignore
						get(nextCtaHotSpotItem, 'time', -1) === get(ctaHotSpotItem, 'time', 0);
					const currentTime = screenShareControls.current?.currentTime() || 0;

					// if (dialogId === 'start' && get(ctaHotSpotItem, 'isStartCta')) break;
					// if (dialogId === 'end' && get(ctaHotSpotItem, 'isEndCta')) break;
					// @ts-ignore
					if (get(ctaHotSpotItem, 'isStartCta')) {
						if (ctaElemStart.disable) break;
						pauseVideo = true;
						const onConfirm = (_dispatch?: Dispatch, clicked?: boolean) => {
							if (clicked) {
								dispatch(setAutoPlay({ data: false, floId, floIndex }));
							}
							checkAndSkipInactivity(get(ctaElemStart, 'id') || '');
							setCurrentElement(null);
							// prevHotspotTime.current = -1;
							dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
							// if (!_dispatch) {
							dispatch(
								skipCta({
									id: get(ctaElemStart, 'id', ''),
									enable: false,
									floId,
									floIndex,
								})
							);
							// }

							dispatch(
								hideDialogAction({
									data: 'start',
									floId,
									floIndex,
								})
							);
							if (screenShareControls.current && !ctaPresentWithHotspotTime) {
								onPlay();
							} else {
								goToStep(undefined, true);
							}
							setIsArrowClick(false);
							return true;
						};
						setShowingElement(true);
						dispatch(
							showDialogAction({
								data: {
									id: 'start',
									visible: !(
										get(ctaElemStart, 'linkedImageId') && isVideoWidthLessThan650
									),
									ctaId: get(ctaElemStart, 'id'),
									disableAutoFocus: isEmbed,
									position: !isVideoWidthLessThan650
										? get(ctaElemStart, 'positionOnScreen')
										: '',
									disablePortal: true,
									fullScreenView: playerDim.height && playerDim.height < 200,
									container: isVideoWidthLessThan650 ? floikFloElement : getContainer,
									classDialogRoot: classNames(styles.classDialogRoot, {
										[styles.interactiveDemoDialogRoot]: interactiveMode,
										[styles.backdropTransparent]: get(ctaElemStart, 'linkedImageId'),
									}),
									headerClassName: styles.headerClassName,
									noBackdropClick: true,
									bodyClassName: styles.bodyClassName,
									classDialogConfirmButton: styles.classDialogConfirmButton,
									header: get(ctaElemStart, 'values.heading', ''),
									centerAlignFooter: true,
									confirmButtonText: get(ctaElemStart, 'values.button.name', ''),
									confirmButtonUrl: get(ctaElemStart, 'values.button.url', ''),
									...getAdventureCtaDialogProperties(ctaElemStart, floId, floIndex),
									onConfirm,
									onCancel: onConfirm, // for adventure cta to continue, normal cta wont have cancel button
								},
								floId,
								floIndex,
							})
						);
					} else if (get(ctaHotSpotItem, 'isEndCta') && ctaElemEnd) {
						if (ctaElemEnd.disable) return;
						pauseVideo = true;
						showEndCta();
					} else {
						if (get(elem, 'disable')) return;
						pauseVideo = true;
						setShowingElement(true);
						dispatch(
							showDialogAction({
								data: {
									id: `cta_${get(elem, 'id')}`,
									position: !isVideoWidthLessThan650 ? get(elem, 'positionOnScreen') : '',
									visible: !(get(elem, 'linkedImageId') && isVideoWidthLessThan650),
									ctaId: get(elem, 'id'),
									disableAutoFocus: isEmbed,
									disablePortal: true,
									fullScreenView: playerDim.height < 200,
									container: isVideoWidthLessThan650 ? floikFloElement : getContainer,
									classDialogRoot: classNames(styles.classDialogRoot, {
										[styles.interactiveDemoDialogRoot]: interactiveMode,
										[styles.backdropTransparent]: get(elem, 'linkedImageId'),
									}),
									headerClassName: styles.headerClassName,
									noBackdropClick: true,
									bodyClassName: styles.bodyClassName,
									classDialogConfirmButton: styles.classDialogConfirmButton,
									header: get(elem, 'values.heading', ''),
									centerAlignFooter: true,
									confirmButtonText: get(elem, 'values.button.name', ''),
									confirmButtonUrl: get(elem, 'values.button.url', ''),
									...getAdventureCtaDialogProperties(elem, floId, floIndex),
									onCancel() {
										setCurrentElement(null);

										dispatch(
											skipCta({
												id: get(elem, 'id', ''),
												enable: false,
												floId,
												floIndex,
											})
										);

										dispatch(
											hideDialogAction({
												data: `cta_${get(elem, 'id')}`,
												floId,
												floIndex,
											})
										);
										if (screenShareControls.current) {
											if (skipInactivity) {
												// @ts-ignore
												checkAndSkipInactivity(get(elem, 'id', ''));
											} else if (ctaPresentWithHotspotTime) {
												goToStep(undefined, true);
											} else {
												screenShareControls.current?.currentTime(currentTime + 0.36);
												screenShareControls.current.play();
												setCurrentElementIndex(-1);
											}
										}

										setIsArrowClick(false);
										return true;
									},
									onConfirm() {
										dispatch(setAutoPlay({ data: false, floId, floIndex }));
										dispatch(
											hideDialogAction({
												data: `cta_${get(elem, 'id')}`,
												floId,
												floIndex,
											})
										);
										const duration = screenShareControls.current?.duration() || 0;

										if (screenShareControls.current) {
											if (duration < currentTime + 0.36) {
												screenShareControls.current.currentTime(0);
												setGotoElementIndex(-1);
												setCurrentElementIndex(-1);
												setCurrentElementTime(-1);
												setCurrentElement(null);
												dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
											} else if (skipInactivity) {
												// @ts-ignore
												checkAndSkipInactivity(get(elem, 'id', ''));
											} else if (ctaPresentWithHotspotTime) {
												goToStep(get(elem, 'id'), true);
											} else {
												screenShareControls.current.currentTime(currentTime + 0.36);
												screenShareControls.current.play();
											}
										}
										dispatch(
											skipCta({ id: get(elem, 'id', ''), enable: false, floId, floIndex })
										);
										setIsArrowClick(false);
										return true;
									},
								},
								floId,
								floIndex,
							})
						);
					}
					screenShareControls.current?.currentTime(get(ctaHotSpotItem, 'time', 0));
				}
			}
			if (pauseVideo) {
				screenShareControls.current?.pause();
			}
			if (resetClickElem) {
				prevClickId.current = '';
				setActivity({
					id: '',
					text: '',
					hasActivity: false,
					pointerPosition: {
						x: 0,
						y: 0,
					},
					elementBounds: {
						x: 0,
						y: 0,
						width: 0,
						height: 0,
					},
					windowSize: {
						width: 0,
						height: 0,
					},
				});
			}
			if (resetCtaElem) {
				dispatch(
					hideDialogAction({
						data: dialogId,
						floId,
						floIndex,
					})
				);
			}
			if (resetHotspotElem) {
				resetHotspot();
			}
		}
	}, [
		setShowingElement,
		isPause,
		currentElement,
		goToStep,
		setActivity,
		resetHotspot,
		// showHotspot,
		setCurrentElement,
		setCurrentElementIndex,
		ctaAndHotspotTimes,
		dialogId,
		currentElementIndex,
		floElements,
		playerDim.width,
		playerDim.height,
		screenShareControls,
		isVideoWidthLessThan650,
		floikFloElement,
	]);

	useEffect(() => {
		if (
			get(currentElement, 'editorElementName') === 'text' &&
			get(currentElement, 'behaviourType') === 'stayFor'
		) {
			setTextElement(currentElement);
			setShowingElement(true);
		} else if (get(currentElement, 'editorElementName') === 'image') {
			setImageElement(currentElement);
			setShowingElement(true);
		} else if (get(currentElement, 'linkedImageId')) {
			let newImageElement = find(get(floElements, 'linked_images'), [
				'id',
				get(currentElement, 'linkedImageId'),
			]);
			if (newImageElement) {
				setImageElement(currentElement);
				setShowingElement(true);
			}
		}
	}, [currentElement, setShowingElement, setImageElement, setTextElement, floElements]);

	const onCtaClick = useCallback(() => {
		if (!screenShareControls?.current) return;
		const currentTime = screenShareControls.current.currentTime() || 0;
		if (screenShareControls.current) {
			screenShareControls.current.currentTime(currentTime + 0.5);
		}
		dispatch(
			hideDialogAction({
				data: '',
				floId,
				floIndex,
			})
		);
	}, [screenShareControls, dialogId, floId]);

	const onCancelCta = useCallback(() => {
		if (!screenShareControls?.current) return;
		dispatch(
			hideDialogAction({
				data: '',
				floId,
				floIndex,
			})
		);
		const currentTime = screenShareControls.current.currentTime() || 0;
		if (screenShareControls.current) {
			screenShareControls.current.currentTime(currentTime + 0.5);
			screenShareControls.current.play();
		}
	}, [screenShareControls, dialogId, floId]);

	const onChangeTime = useCallback(
		(e?: unknown) => {
			if (dialogId) {
				return;
			}
			if (!screenShareControls.current) return;
			const currentTime = screenShareControls.current.currentTime() || 0;
			const duration = screenShareControls.current.duration() || 0;

			sendCompletionStatus(currentTime);
		},
		[
			videoReady,
			onCancelCta,
			onCtaClick,
			screenShareControls,
			duration,
			floElements,
			hasNotPlayed,
			getContainer,
			isArrowClick,
			dialogId,
			sendCompletionStatus,
		]
	);

	const onTimeUpdate = useCallback(
		(e?: unknown) => {
			if (!screenShareControls.current) return;
			const timestamp = screenShareControls.current.currentTime();
			const targetAnnotations = findIndex(annotationsMeta, (annot) => {
				return timestamp - 0.002 <= annot.time && annot.time <= timestamp + 0.3;
			});
			if (targetAnnotations >= 0) {
				// @ts-ignore
				setCurrentAnnotations(first(annotations));
				dispatch(
					getAnnotationsByTime({
						time: annotationsMeta[targetAnnotations].time,
						floId: floId || '',
						commentId: selectedCommentId,
						floIndex,
					})
				);
			} else if (!screenShareControls.current?.paused() && tool !== 'none') {
				setCurrentAnnotations(undefined);
				if (!disableAnnotations)
					dispatch(setAnnotationToolAction({ data: 'none', floId, floIndex }));
			} else if (targetAnnotations < 0 && screenShareControls.current?.paused()) {
			}
			setEnableAddComment({
				visible: false,
				top: 0,
				left: 0,
				question: '',
				name: '',
				email: '',
				className: '',
			});
		},
		[
			screenShareControls,
			disableAnnotations,
			tool,
			annotationsMeta,
			currentAnnotations,
			selectedCommentId,
		]
	);
	const onPlaybackRatesChange = useCallback(
		(rate: number) => {
			if (screenShareControls.current) {
				screenShareControls.current.playbackRate(rate);
			}
			if (videoControls.current) {
				videoControls.current.playbackRate(rate);
			}
			if (audioControls.current) {
				audioControls.current.playbackRate(rate);
			}
		},
		[screenShareControls.current, videoControls.current, audioControls.current]
	);

	const playingRef = useRef(false);

	const onPlay = useCallback(
		(from?: string) => {
			onPlayBgCb();
			if (!videoReady) {
				if (screenShareControls.current) {
					// @ts-ignore
					screenShareControls.current.currentTime(0);
					screenShareControls.current?.load();
				}
				//@ts-ignore
				if (videoControls.current) {
					videoControls.current.currentTime(0);
					videoControls.current?.load();
				}
				return;
			}
			if (get(appComment, 'show')) {
				dispatch(
					setAppComment({
						data: {
							show: false,
						},
						floId,
						floIndex,
					})
				);
			}
			if (dialogId) {
				dispatch(
					hideDialogAction({
						data: '',
						floId,
						floIndex,
					})
				);
				if (isFunction(dialogOnStartConfirm)) {
					// @ts-ignore
					dialogOnStartConfirm();
				}
			}
			if (hasNotPlayed) {
				const skipPlay =
					get(ctaAndHotspotTimes, [0, 'time']) === 0 &&
					get(ctaAndHotspotTimes, [0, 'type']) !== 'cta';
				if (skipPlay) {
					dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
					setGotoElementIndex(0);
					return;
				}
			}

			if (skipVideo) {
				goToStep(undefined, true);
			} else if (!playingRef.current) {
				if (
					from !== 'update' &&
					screenShareControls.current &&
					screenShareControls.current.paused()
				) {
					playingRef.current = true;
					// @ts-ignore
					screenShareControls.current.play()?.then(() => {
						playingRef.current = false;
					});
				}
				//@ts-ignore
				if (videoControls.current && videoControls.current.paused()) {
					videoControls.current.play()?.catch(console.error);
					videoControls.current.currentTime(
						screenShareControls?.current?.currentTime() || 0
					);
				}

				//@ts-ignore
				if (audioControls.current && audioControls.current.paused())
					audioControls.current.play()?.catch(console.error);
			}
			dispatch(
				setScrollTo({
					scrollInfo: {
						commentId: '',
						highlightCommentId: '',
					},
					floId,
					floIndex,
				})
			);
		},
		[
			autoPlay,
			playingRef,
			videoReady,
			hasNotPlayed,
			ctaAndHotspotTimes,
			currentElement,
			currentElementIndex,
			setCurrentElementIndex,
			goToStep,
			skipVideo,
			appComment,
			resetHotspot,
			screenShareControls.current,
			videoControls.current,
			audioControls.current,
			dialogId,
			dialogOnStartConfirm,
			onPlayBgCb,
		]
	);
	onPlayRef.current = onPlay;
	const onUpdate = useCallback(
		(isPlaying?: boolean) => {
			if (screenShareControls.current) {
				// @ts-ignore
				const paused = isPlaying ? false : screenShareControls.current.paused();
				if (!paused) {
					if (onPlayRef?.current) onPlayRef.current('onupdate');
				} else {
					console.trace('pausing');
					onPause();
				}
				setPause(paused);
			}
		},
		[setPause, onPlayRef, onPause, screenShareControls]
	);

	const onUpdateRef = useRef(onUpdate);

	const onSeeking = useCallback(
		(params: unknown) => {
			const timestamp =
				Math.round((screenShareControls.current?.currentTime() || 0) * 100) / 100;
			const isPaused = screenShareControls.current?.paused();
			if (seekTime !== timestamp) {
				// prevHotspotTime.current = -1;
				setLocalSeekTime(timestamp || 0);
				setIsSeeking(true);
			}
			if (!isPaused) {
				setCurrentElement(null);
			}
		},
		[
			/*prevHotspotTime, */ setCurrentElement,
			videoControls,
			setIsSeeking,
			screenShareControls,
			seekTime,
		]
	);

	const onSeeked = useCallback(
		(event: unknown) => {
			setIsSeeking(false);
			//@ts-ignore
			if (!screenShareControls) return;
			//@ts-ignore
			const timestamp = screenShareControls.current.currentTime();
			if (videoControls.current) {
				videoControls.current?.currentTime(timestamp);
			}
			if (audioControls.current) {
				audioControls.current?.currentTime(timestamp);
			}
			if (isPause) {
				onTimeUpdate();
			} else {
				dispatch(setAnnotationToolAction({ data: 'none', floId, floIndex }));
				dispatch(setAnnotationsByTime({ floId, floIndex }));
				dispatch(
					setScrollTo({
						scrollInfo: {
							commentId: '',
							highlightCommentId: '',
						},
						floId,
						floIndex,
					})
				);
			}
		},
		[
			setIsSeeking,
			isPause,
			onTimeUpdate,
			screenShareControls.current,
			videoControls.current,
			audioControls.current,
			dialogId,
		]
	);

	const onVolumeChange = useCallback(
		({ isMuted, volume }: { isMuted: boolean; volume: any }) => {
			const usingVideoControls =
				!isAiVoice && get(secondaryPlayerOption, 'sources[0].id');
			//@ts-ignore
			if (videoControls.current && usingVideoControls) {
				//@ts-ignore
				videoControls.current.volume(isNoVoice || isMuted ? 0 : volume);
				videoControls.current?.muted(isNoVoice || isMuted);
			}
			//@ts-ignore
			if (screenShareControls.current && !usingVideoControls) {
				//@ts-ignore
				screenShareControls.current.volume(isNoVoice || isMuted ? 0 : volume);
				screenShareControls.current?.muted(isNoVoice || isMuted);
				//@ts-ignore
			}
			// if (audioControls.current) {
			// 	//@ts-ignore
			// 	audioControls.current.volume(isMuted ? 0 : volume);
			// 	audioControls.current?.muted(isMuted);
			// }
			setVolume(isNoVoice || isMuted ? 0 : volume);
		},
		[
			isNoVoice,
			isAiVoice,
			get(secondaryPlayerOption, 'sources[0].id'),
			setVolume,
			screenShareNode,
			videoControls.current,
			screenShareControls.current,
			audioControls,
			elementVoiceResources,
		]
	);

	useEffect(() => {
		if (
			elementsVoiceEnabled ||
			(hasBgMusic &&
				!get(backgroundMusicResource, 'properties.pauseOnInteraction', false) &&
				get(ctaAndHotspotTimes, '[0].time') === 0)
		) {
			onVolumeChange({ isMuted: true, volume: 0 });
		}
	}, [elementsVoiceEnabled, onVolumeChange, hasBgMusic, backgroundMusicResource]);

	const {
		onElementVoiceMuteClick: toggleElementVoiceMute,
		showElementVoiceIcon,
		subTitle,
		isElementVoicePlaying,
		onReplayClick,
		elementVoiceTooltipTitle,
	} = useElementVoicePlayer(
		currentElement,
		elementVoiceResources,
		floElements,
		volume === 0,
		onVolumeChange,
		hasNotPlayed
	);

	const onElementVoiceMuteClick = useCallback(() => {
		toggleElementVoiceMute();
		onToggleMuteBgCb();
	}, [toggleElementVoiceMute, onToggleMuteBgCb]);

	const onContinuePlay = useCallback(() => {
		setCurrentAnnotations(undefined);
		dispatch(setAnnotationsByTime({ floId, floIndex }));
		setEnableAddComment({
			visible: false,
			top: 0,
			left: 0,
			question: '',
			name: '',
			email: '',
			className: '',
		});
		dispatch(setAnnotationToolAction({ data: 'none', floId, floIndex }));
		onPlay();
	}, [setCurrentAnnotations, setEnableAddComment, onPlay]);

	useEffect(() => {
		if (tool === 'none') {
			setEnableAddComment({
				visible: false,
				top: 0,
				left: 0,
				question: '',
				name: '',
				email: '',
				className: '',
			});
		}
	}, [tool, isPause, setEnableAddComment]);

	const onResize = useCallback(() => {
		if (!screenShareNode?.current) return;
		setLeftSeekPosition(
			(Math.abs(
				(get(containerRef, 'current.offsetWidth') || 0) -
					(get(screenShareNode, 'current.offsetWidth') ||
						get(containerRef, 'current.offsetWidth') ||
						0)
			) || 8) /
				2 +
				9
		);
		if (videoNode?.current) {
			const mainNodeboundingRect = secondaryVideoRef?.current?.getBoundingClientRect();
			const videoNodeElement = document.getElementById(videoNode?.current.id);
			const { width, height } = videoNodeElement?.getBoundingClientRect() || {
				width: 0,
				height: 0,
			};
			if (boundToContainer && containerRef?.current) {
				if (videoPlayerDim.initial) {
					setLeftPosition(0);
					setBottomPosition(get(mainNodeboundingRect, 'height', 0) - height);
				}

				if (
					get(presenterVideoOptions, 'width', 0) > playerDim.width * 0.7 ||
					get(presenterVideoOptions, 'height', 0) > playerDim.height * 0.7 ||
					get(presenterVideoOptions, 'width', 0) < playerDim.width * 0.2 ||
					get(presenterVideoOptions, 'height', 0) < playerDim.height * 0.2
				) {
					const miniPlayerDim = calculateAspectRatioFit(
						get(secondaryPlayerOption, 'sources[0].width', 0),
						get(secondaryPlayerOption, 'sources[0].height', 0),
						playerDim.width * 0.3,
						playerDim.height * 0.3
					);
					setVideoPlayerDim((data) => ({
						...data,
						width: miniPlayerDim.width,
						height: miniPlayerDim.height,
					}));
					setPresenterVideoOptions({
						...DEFAULT_PRESENTER_OPTIONS,
						width: miniPlayerDim.width,
						height: miniPlayerDim.height,
					});
				}
			} else {
			}
		}
	}, [
		secondaryPlayerOption,
		presenterVideoOptions,
		setBounds,
		boundToContainer,
		playerDim,
		leftPosition,
		bottomPosition,
		setLeftSeekPosition,
		setVideoPlayerDim,
		setLeftPosition,
		setBottomPosition,
		setPresenterVideoOptions,
		containerRef,
		screenShareNode,
		videoNode,
		scrollTopPosition,
		windowDimensions.width,
		windowDimensions.height,
	]);

	useEffect(() => {
		if (!videoReady || !screenShareNode.current) return;
		const onMouseEnter = () => {
			setProgressHoverState(true);
		};
		const onMouseLeave = () => {
			setProgressHoverState(false);
		};
		const progressControl = document?.querySelector('.vjs-progress-control');
		if (progressControl) {
			progressControl.addEventListener('mouseenter', onMouseEnter, false);
			progressControl.addEventListener('mouseleave', onMouseLeave, false);
		}
		let containerLeft =
			Math.abs(
				(get(containerRef, 'current.offsetWidth') || 0) -
					(get(screenShareNode, 'current.offsetWidth') || 0)
			) / 2;
		if (boundToContainer && containerRef?.current) {
			const { x } = containerRef?.current.getBoundingClientRect();
			containerLeft = x;
		}
		console.log('leftPosition 3', containerLeft);
		setLeftPosition(containerLeft);
		setLeftSeekPosition(
			(Math.abs(
				(get(containerRef, 'current.offsetWidth') || 0) -
					(get(screenShareNode, 'current.offsetWidth') ||
						get(containerRef, 'current.offsetWidth') ||
						0)
			) || 8) /
				2 +
				9
		);
		const miniPlayerDim = calculateAspectRatioFit(
			get(secondaryPlayerOption, 'sources[0].width', 0),
			get(secondaryPlayerOption, 'sources[0].height', 0),
			playerDim.width * 0.2,
			playerDim.height * 0.2
		);
		if (containerRef?.current) {
			setBottomPosition(playerDim.height - miniPlayerDim.height - (hideHeader ? 60 : 0));
			setVideoPlayerDim((dims) => ({
				...dims,
				height: miniPlayerDim.height,
				width: miniPlayerDim.width,
			}));
			setPresenterVideoOptions({
				...DEFAULT_PRESENTER_OPTIONS,
				height: miniPlayerDim.height,
				width: miniPlayerDim.width,
			});
		}
		return () => {
			if (progressControl) {
				progressControl.removeEventListener('mouseenter', onMouseEnter, false);
				progressControl.removeEventListener('mouseleave', onMouseLeave, false);
			}
		};
	}, [
		videoReady,
		screenShareNode,
		containerRef,
		setVideoPlayerDim,
		secondaryPlayerOption,
		setPresenterVideoOptions,
		setLeftPosition,
		setBottomPosition,
		setProgressHoverState,
	]);

	const onAnnotationClick = (commentId: string) => {
		onPause();
		dispatch(
			setScrollTo({
				scrollInfo: {
					commentId,
					highlightCommentId: commentId,
				},
				floId,
				floIndex,
			})
		);
	};

	const handleClose = useCallback(() => {
		setEnableAddComment({
			visible: false,
			top: 0,
			left: 0,
			question: '',
			name: '',
			email: '',
			className: '',
		});

		dispatch(updateAnnotationAction({ floIndex, floId }));
	}, []);

	const onSetReadyCallback = useCallback(
		(val: boolean) => {
			setPrimaaryVideoReady(val);
		},
		[setPrimaaryVideoReady]
	);

	const onVideoSetReadyCallback = useCallback(
		(val: boolean) => {
			setPresenterVideoReady(val);
		},
		[setPresenterVideoReady]
	);

	const onHotSpotClick = useCallback(() => {
		setIsArrowClick(false);
		const newIndex = currentElementIndex + 1;
		const currElemTime = get(ctaAndHotspotTimes, [currentElementIndex, 'time'], -1);
		const newElemTime = get(ctaAndHotspotTimes, [newIndex, 'time'], -1);
		dispatch(
			trackHotspotAction({
				id: hotspot.current.id,
				floId,
				floIndex,
			})
		);

		if (
			newElemTime >= 0 &&
			newElemTime >= currElemTime &&
			newElemTime <= currElemTime + 0.36
		) {
			setGotoElementIndex(newIndex);
			if (newElemTime !== currElemTime) {
				// @ts-ignore
				screenShareControls.current.currentTime(newElemTime);
			}
		} else if (screenShareControls.current) {
			const play = checkAndSkipInactivity(hotspot.current.id);
			if (play) {
				// prevHotspotTime.current = hotspot.current.time;
				screenShareControls.current.play();
			}
		}
		dispatch(setAutoPlay({ data: false, floId, floIndex }));
	}, [
		currentElementIndex,
		setGotoElementIndex,
		screenShareControls,
		hotspot,
		hotspotsGroups,
		setHotspot,
		ctaAndHotspotTimes,
		checkAndSkipInactivity,
		setIsArrowClick,
	]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			try {
				if (screenShareNode?.current) {
					if (
						event.target &&
						!includes(
							['TEXTAREA', 'INPUT', 'VIDEO'],
							upperCase(get(event, 'target.tagName', ''))
						) &&
						// @ts-ignore
						event.target.getAttribute('contenteditable') !== 'true'
					) {
						screenShareNode?.current.focus();

						const forwardedEvent = new KeyboardEvent('keydown', {
							key: event.key,
							keyCode: event.keyCode,
							code: event.code,
							bubbles: true, // set to true to allow event propagation
							cancelable: true, // set to true to allow event cancellation
							composed: true, // set to true to allow the event to cross the shadow DOM boundary
						});

						// Dispatch the new event on the target element
						screenShareNode.current.dispatchEvent(forwardedEvent);
					}
				}
			} catch (e) {
				// console.error(e);
				dispatch(
					analyticsLog({
						level: 'error',
						message: `onkeydown ${location.href}`,
						value: get(e, 'stack'),
					})
				);
			}
		};
		window.addEventListener('keydown', onKeyDown, true);
		return () => {
			window.removeEventListener('keydown', onKeyDown, true);
		};
	}, [screenShareNode]);

	useEffect(() => {
		if (!containerRef?.current || !screenShareNode.current) return;
		const observer = new ResizeObserver(onResize);
		observer.observe(screenShareNode.current);
		return () => {
			observer.disconnect();
		};
	}, [onResize, containerRef?.current, screenShareNode.current]);

	const newVideoSize = calculateAspectRatioFit(
		videoSize?.width || 16,
		videoSize?.height || 9,
		playerDim.width,
		playerDim.height
	);

	const offsetLeft = Math.abs(newVideoSize.width - playerDim.width) / 2;
	const offsetTop =
		(playerDim.height - newVideoSize.height - (interactiveMode ? 0 : 0)) / 2;

	const addAnnotationHandler = useCallback(
		(data: AnnotationSnapshot, event?: KonvaEventObject<MouseEvent>) => {
			const value = last(data.state) || { x: 0, y: 0, height: 0, width: 0 };
			let offset = 0;
			if (containerRef?.current) {
				const { x } = containerRef.current.getBoundingClientRect();
				offset = x;
			}
			setEnableAddComment({
				visible: !isFloClosed && !disableAnnotations,
				top: min([
					get(value, 'y', 0) + 20,
					max([windowDimensions.height - 280, 0]),
				]) as number,
				left: min([
					offset + get(value, 'x', 0) + get(value, 'width', 0) - 20,
					max([windowDimensions.width - 220, 0]),
				]) as number,
				question: '',
				name: Cookie.get('floik-username') || '',
				email: Cookie.get('floik-user-email') || '',
				className: '',
			});
			dispatch(
				addAnnotationsAction({
					data,
					floId: floId || '',
					floSpaceId: floSpaceId || '',
					trackId: get(mainPlayerOption, 'sources[0].id'),
					floIndex,
				})
			);
		},
		[
			containerRef?.current,
			setEnableAddComment,
			isFloClosed,
			disableAnnotations,
			windowDimensions.width,
			windowDimensions.height,
		]
	);
	const [croppedVideoStyles, setCroppedVideoStyles] = useState({});
	const [hotSpotCropStyles, setHotSpotCropStyles] = useState<
		| (CSSProperties & {
				transitionTime?: number;
				scaleRatio?: number;
				translateX?: number;
				translateY?: number;
		  })
		| undefined
		| null
	>();
	const [hotspotCropConfig, setHotspotCropConfig] = useState<{
		enable: boolean;
		values: {
			x: number;
			x2: number;
			y: number;
			y2: number;
		};
	} | null>(null);
	const isCropped = appliedCrop?.enable;

	const onRestart = useCallback(() => {
		onRestartBgCb();
		screenShareControls.current?.currentTime(0);
		setGotoElementIndex(-1);
		setlastElementIndex(-1);
		setCurrentElementIndex(-1);
		setCurrentElementTime(-1);
		setCurrentElement(null);
		const ctas = [
			...get(floElements, 'info_ctastart', []),
			...get(floElements, 'cta', []),
			...get(floElements, 'info_ctaend', []),
		];
		ctas.forEach((cta) => {
			dispatch(
				skipCta({
					id: get(cta, 'id', ''),
					enable: true,
					floId,
					floIndex,
				})
			);
		});
	}, [
		floElements,
		screenShareControls,
		setGotoElementIndex,
		setlastElementIndex,
		setCurrentElementIndex,
		setCurrentElementTime,
		setCurrentElement,
		onRestartBgCb,
	]);

	useEffect(() => {
		if (hotspot && hotspot.current?.id && get(hotspot, 'current.enableZoom')) {
			const zoomWidth = hotspot.current.windowSize.width * 0.25;
			const zoomHeight = hotspot.current.windowSize.height * 0.25;
			let posX = get(hotspot, 'current.pointerPosition.x') - zoomWidth;
			let diffX = 0;
			let diffX2 = 0;
			if (posX < 0) {
				diffX = Math.abs(posX);
			}

			let posX2 = get(hotspot, 'current.pointerPosition.x') + zoomWidth + diffX;
			if (posX2 > get(hotspot, 'current.windowSize.width')) {
				diffX2 = posX2 - get(hotspot, 'current.windowSize.width');
			}
			let posY = get(hotspot, 'current.pointerPosition.y') - zoomHeight;
			let diffY2 = 0;
			let diffY = 0;
			if (posY < 0) {
				diffY = Math.abs(posY);
			}
			let posY2 = get(hotspot, 'current.pointerPosition.y') + zoomHeight + diffY;
			if (posY2 > get(hotspot, 'current.windowSize.height')) {
				diffY2 = posY2 - get(hotspot, 'current.windowSize.height');
			}

			const values = {
				enable: true,
				values: {
					x: get(hotspot, 'current.pointerPosition.x') - zoomWidth + diffX - diffX2,
					y: get(hotspot, 'current.pointerPosition.y') - zoomHeight + diffY - diffY2,
					x2: get(hotspot, 'current.pointerPosition.x') + zoomWidth - diffX2 + diffX,
					y2: get(hotspot, 'current.pointerPosition.y') + zoomHeight - diffY2 + diffY,
					windowSize: hotspot.current.windowSize,
				},
				videoSize: hotspot.current.windowSize,
			};

			const prevX = get(prevHotspotRef, 'current.x', 0) as number;
			const prevY = get(prevHotspotRef, 'current.y', 0) as number;
			const xDiff = get(values, 'values.x') - prevX;
			const yDiff = get(values, 'values.y') - prevY;
			const transitionTime = Math.sqrt(xDiff ** 2 + yDiff ** 2);
			// @ts-ignore
			prevHotspotRef.current = values.values;

			const value = calculateVideoCropMatrix(
				playerDim,
				hotspot.current.windowSize,
				// @ts-ignore
				values,
				false
			);
			setHotspotCropConfig(values);
			// @ts-ignore
			setHotSpotCropStyles({
				...value,
				transitionTime: max([40000 / transitionTime, 500]) as number,
			});
		} else {
			// @ts-ignore
			prevHotspotRef.current = null;
			const value = calculateVideoCropMatrix(
				playerDim,
				get(hotspot, 'current.windowSize') || playerDim,
				// @ts-ignore
				{
					values: undefined,
				},
				false
			);
			setHotSpotCropStyles(value);
			setHotspotCropConfig(null);
		}
	}, [
		hotspot?.current,
		hotspot?.current?.id,
		setHotSpotCropStyles,
		setHotspotCropConfig,
		playerDim,
	]);

	const onArrowClickCb = useCallback(
		(curIndex: number, next?: boolean) => {
			const newElement = get(ctaAndHotspotTimes, `[${curIndex + (next ? 1 : -1)}]`);
			if (
				newElement &&
				get(newElement, 'time') !== screenShareControls.current?.currentTime()
			) {
				screenShareControls.current?.currentTime(get(newElement, 'time', 0));
			}
			if (hotspot.current.id && next) {
				dispatch(
					trackHotspotAction({
						id: hotspot.current.id,
						body: {
							buttonType: 'next',
						},
						floId,
						floIndex,
					})
				);
			} else {
				const currentTime = screenShareControls?.current?.currentTime();
				dispatch(
					trackClickAction({
						body: {
							videoTime: currentTime,
							buttonType: next ? 'next' : 'previous',
						},
						floId,
						floIndex,
					})
				);
			}

			setGotoElementIndex(curIndex + (next ? 1 : -1));
			// setCurrentElementTime(get(ctaAndHotspotTimes, `[${curIndex + (next ? 1 : -1)}].time`, -1));
		},
		[hotspot, screenShareControls, setGotoElementIndex, ctaAndHotspotTimes]
	);

	const miniPlayerMaxDim = calculateAspectRatioFit(
		get(secondaryPlayerOption, 'sources[0].width', 0),
		get(secondaryPlayerOption, 'sources[0].height', 0),
		playerDim.width || 0,
		playerDim.height || 0
	);
	const progressRef = React.useRef<HTMLDivElement>();
	const durationRef = React.useRef<HTMLDivElement>();

	const [inFullScreen, setIsFullScreen] = useState(false);
	const trimConfig = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim`)
	);
	const isTrimEnable = get(trimConfig, 'enable');
	const timeValue = get(trimConfig, 'timeValue', []);

	const onKeyBoardProgress = useCallback((fromTime: number, toTime: number) => {
		if (fromTime > toTime) {
			analyticsMetricsHelper.hasManualSeekedForward = true;
		}
	}, []);

	const onClickProgressBar = useCallback(
		(
			e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
			cb?: Function
		) => {
			preventDefaultForTouchEndEvent(e);
			const target = document.getElementById(`floikCustomProgressBar_${floIndex}`);
			// @ts-ignore
			const { width, left } = target.getBoundingClientRect();
			let clientX = 0;
			if (e.type === 'touchend') {
				// @ts-ignore
				clientX = e.changedTouches[0].pageX - left;
			} else {
				clientX = get(e, 'nativeEvent.offsetX', 0);
			}
			const actualCurrentTime = screenShareControls?.current?.currentTime() || 0;
			let newDuration = isEmpty(timeValue)
				? screenShareControls?.current?.duration()
				: timeValue[1] - timeValue[0];
			if (!newDuration) return;
			const ratio = clientX / width;
			const currentTime = ratio * newDuration;
			let newTime = currentTime;
			if (isTrimEnable && isFinite(timeValue[0])) {
				newTime = currentTime + timeValue[0];
				screenShareControls?.current?.currentTime(newTime);
				onKeyBoardProgress(actualCurrentTime, newTime);
			} else {
				screenShareControls?.current?.currentTime(newTime);
				onKeyBoardProgress(actualCurrentTime, newTime);
			}
			cb?.(newTime);
		},
		[onKeyBoardProgress, isTrimEnable, timeValue, screenShareControls, floIndex]
	);

	const onPresenterVideoResize = useCallback(
		(e: React.SyntheticEvent, data: { size: { width: number; height: number } }) => {
			if (!screenShareNode.current) return;
			setVideoPlayerDim({
				initial: false,
				width: data.size.width,
				height: data.size.height,
			});
			setPresenterVideoOptions({
				...DEFAULT_PRESENTER_OPTIONS,
				width: data.size.width,
				height: data.size.height,
			});
			if (boundToContainer && containerRef?.current && secondaryVideoRef?.current) {
				const mainVideoNodeBoundRect =
					secondaryVideoRef?.current?.getBoundingClientRect();
				if (leftPosition < mainVideoNodeBoundRect.left + 20) {
					setLeftPosition(mainVideoNodeBoundRect.left);
				} else if (leftPosition > mainVideoNodeBoundRect.right) {
					setLeftPosition(mainVideoNodeBoundRect.right);
				}
				if (bottomPosition > mainVideoNodeBoundRect.bottom - data.size.height) {
					setBottomPosition(mainVideoNodeBoundRect.bottom - data.size.height);
				} else if (bottomPosition < mainVideoNodeBoundRect.top) {
					setBottomPosition(mainVideoNodeBoundRect.top);
				}
			} else {
			}
		},
		[
			screenShareNode,
			setVideoPlayerDim,
			setPresenterVideoOptions,
			boundToContainer,
			containerRef,
			setBounds,
			leftPosition,
			setLeftPosition,
			bottomPosition,
			setBottomPosition,
			windowDimensions.width,
			windowDimensions.height,
			secondaryVideoRef.current
		]
	);
	const onVideoEnded = useCallback(() => {
		setGotoElementIndex(-1);
		setCurrentElementIndex(-1);
		setCurrentElementTime(-1);
		setCurrentElement(null);
		dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
		onVideoEndBgCb();

		const ctaElemStart: publishElementsDataType = get(floElements, 'info_ctastart')?.[0];
		const ctaElemEnd: publishElementsDataType = get(floElements, 'info_ctaend')?.[0];
		const allCTas = [...(floElements.cta || []), ctaElemStart, ctaElemEnd];

		allCTas.forEach((ctaEvent) => {
			dispatch(skipCta({ id: get(ctaEvent, 'id'), enable: true, floId, floIndex }));
		});
	}, [
		floElements,
		setGotoElementIndex,
		setCurrentElementIndex,
		setCurrentElementTime,
		setCurrentElement,
		onVideoEndBgCb,
	]);

	useEffect(() => {
		if (isAiVoice && videoControls.current) {
			videoControls.current.muted(true);
		}
	}, [videoControls, isAiVoice]);

	const resetPrevHotspot = useCallback(() => {
		// prevHotspotTime.current = -1;
		resetHotspot();
	}, [/*prevHotspotTime,*/ resetHotspot]);

	useEffect(() => {
		if (get(appComment, 'show')) {
			onPause();
			setTimeout(() => {
				if (screenShareControls.current) {
					screenShareControls.current.currentTime(get(appComment, 'time', 0));
					const elements = {
						time: get(appComment, 'time'),
						uid: get(appComment, 'id'),
						state: [
							{
								key: get(appComment, 'id'),
								tool: 'rect',
								time: get(appComment, 'time'),
								windowSize: get(appComment, 'canvasRenderConfig.windowCoordinates'),
								x: get(appComment, 'canvasRenderConfig.canvasCoordinates.x'),
								y: get(appComment, 'canvasRenderConfig.canvasCoordinates.y'),
								width: get(appComment, 'canvasRenderConfig.canvasCoordinates.width'),
								height: get(appComment, 'canvasRenderConfig.canvasCoordinates.height'),
								points: [],
							},
						],
					};
					dispatch(
						hideDialogAction({
							data: '',
							floId,
							floIndex,
						})
					);
					dispatch(
						addAnnotationsAction({
							data: elements,
							floId: '',
							floSpaceId: '...',
							floIndex,
						})
					);
				}
			}, 100);
		}
	}, [appComment, screenShareControls]);

	useEffect(() => {
		const isReady =
			(isAiVoice || !get(secondaryPlayerOption, 'sources[0].id')
				? true
				: presenterVideoReady) && primaryVideoReady;
		setReady(isReady);
		if (isFunction(setVideoReady)) {
			setVideoReady(isReady);
		}
	}, [
		presenterVideoReady,
		primaryVideoReady,
		setReady,
		setVideoReady,
		isAiVoice,
		get(secondaryPlayerOption, 'sources[0].id'),
	]);

	useEffect(() => {
		if (microApp && videoReady) {
			removeFloikLoaderInMicroApp(floIndex);
			// window.doc360PreviewInstance?.onMessageFromMicroApp({
			// 	type: 'floik_app_flo_rendered',
			// 	payload: {
			// 		status: 'done',
			// 		floId,
			// 	},
			// });
		}
	}, [videoReady, floIndex]);

	// useEffect(() => {
	// 	if (
	// 		autoPlay
	// 		&& videoReady
	// 		&& Math.round(currentTimeSeconds * 100) / 100 === 0
	// 		&& get(ctaAndHotspotTimes, [0, 'time']) === 0
	// 		&& Math.max(currentElementIndex, 0) === 0
	// 	) {
	// 		onPause();
	// 	}
	// }, [
	// 	autoPlay,
	// 	onPause,
	// 	currentTimeSeconds,
	// 	currentElementIndex,
	// 	videoReady,
	// 	setGotoElementIndex,
	// 	ctaAndHotspotTimes
	// ])

	const toggleCountDownPause = useCallback(
		(val: boolean) => {
			if (val) {
				onVideoPauseBgCb();
			} else {
				onPlayBgCb();
			}
		},
		[onVideoPauseBgCb, onPlayBgCb]
	);

	const onLoadedData = useCallback(() => {
		if (
			autoPlay &&
			Math.round(currentTimeSeconds * 100) / 100 === 0 &&
			get(ctaAndHotspotTimes, [0, 'time']) === 0
		) {
			onPause();
		}
	}, [onPause, autoPlay, currentTimeSeconds, ctaAndHotspotTimes, currentElementIndex]);

	const hideElement = useCallback(() => {
		console.log('hideElement');
		if (imageElement) setImageElement(undefined);
		if (textElement) setTextElement(undefined);
		goToStep(undefined, true);
	}, [goToStep, setImageElement, setTextElement]);

	const onTimeRefCallback = useCallback(() => {
		hideElement();
		// timerRef.current = undefined;
	}, [timerRef, hideElement]);

	useEffect(() => {
		if (
			autoPlay &&
			imageElement &&
			isPause &&
			get(imageElement, 'time') - currentTimeSeconds < 0.01 &&
			get(imageElement, 'behaviourType') === 'stayFor'
		) {
			if (timerRef?.current) {
				clearTimeout(timerRef.current);
				return;
			}
		}

		if (
			autoPlay &&
			textElement &&
			isPause &&
			get(textElement, 'time') - currentTimeSeconds < 0.01 &&
			get(imageElement, 'behaviourType') === 'stayFor'
		) {
			if (timerRef?.current) {
				clearTimeout(timerRef.current);
				return;
			}
		}
	}, [textElement, imageElement, isPause, currentTimeSeconds, timerRef]);

	useEffect(() => {
		if (!autoPlay) {
			clearTimeout(timerRef.current);
			return;
		}
		if (currentElementIndex < 0) {
			if (timerRef.current) clearTimeout(timerRef.current);
			return;
		}

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		if (showingElement) {
			const { voiceProperties } = getElementVoiceResources(
				currentElement,
				elementVoiceResources,
				floElements
			);
			const timeout =
				get(voiceProperties, 'durationInMillis', 0) > 3000
					? get(voiceProperties, 'durationInMillis', 0)
					: 3000;
			timerRef.current = setTimeout(onTimeRefCallback, timeout);
		}
	}, [
		autoPlay,
		isPause,
		onTimeRefCallback,
		hideElement,
		setShowingElement,
		showingElement,
		currentElementIndex,
		elementVoiceResources,
		currentElement,
		floElements,
	]);

	const showVoiceIcon =
		(hasAudioTrack &&
			volume === 0 &&
			!screenShareControls.current?.paused() &&
			subtitlesList.some(
				(subtitle: Subtitle) =>
					currentTime >= subtitle.startTime && currentTime <= subtitle.endTime
			)) ||
		(hasBgMusic &&
			volume === 0 &&
			!hideVoiceIcon &&
			(currentTime !== 0 || get(ctaAndHotspotTimes, '[0].time') === 0));

	return (
		<>
			{hidePlayer && (
				<div className={styles.dimensionErrorMessage}>{DIMENSIONS_ERROR_MESSAGE}</div>
			)}
			<div
				ref={videoContainerRef}
				className={classNames(
					'videoPlayerContainer',
					className,
					styles.videoPlayerContainer,
					{
						[styles.withBorder]: withBorder,
						[styles.fullScreenContainer]: hideHeader && !internalArticles,
						[styles.hidePlayer]: hidePlayer,
					}
				)}
			>
				{get(containerRef, 'current.offsetWidth') && showWrapper && (
					<Wrapper
						theme={wrapper}
						className={{ [styles.wrapperElement]: true }}
						styleObj={{ width: playerDim.width }}
					/>
				)}
				<CustomInVideoControls
					currentElementType={
						get(currentElement, 'time') === currentTimeSeconds
							? get(currentElement, 'editorElementName', '')
							: ''
					}
					floId={floId}
					floIndex={floIndex}
					setCountDownPause={setImageCountDownPause}
					showSeekbar={
						imageCountDownPause && get(currentElement, 'behaviourType') !== 'nextButton'
					}
					hidePlayIcon={
						!hasNotPlayed &&
						get(currentElement, 'time') === currentTimeSeconds &&
						includes(
							['cta', 'hotspots', 'clicks', 'image', 'text'],
							get(currentElement, 'editorElementName', '')
						)
					}
					autoPlay={autoPlay}
					sliderIndex={lastElementIndex}
					setCurrentElementIndex={setGotoElementIndex}
					onRestart={onRestart}
					onArrowClickCb={onArrowClickCb}
					resetLastSeekedTime={resetLastSeekedTime}
					hidePlayer={hidePlayer}
					isEmbed={isEmbed}
					resetPrevHotspot={resetPrevHotspot}
					hasAudioTrack={hasAudioTrack || hasBgMusic}
					activityPosition={activityPosition}
					isAiVoice={isAiVoice}
					showEndCta={showEndCta}
					goToStep={goToStep}
					isTabView={isTabView}
					hasInitialhotspot={get(hotspot.current, 'time', -1) === 0}
					style={{
						width: playerDim.width,
						height: playerDim.height,
					}}
					onPlay={onPlay}
					onPlayRef={onPlayRef}
					className={classNames({
						[styles.interactiveDemo]: withBorder && !inFullScreen && interactiveMode,
					})}
					onPause={onPause}
					setIsFullScreen={setIsFullScreen}
					isFullScreen={inFullScreen}
					addAnnotationHandler={addAnnotationHandler}
					nodeRef={screenShareControls}
					presenterNodeRef={videoControls}
					usePresenterNodeForVolume={
						!isAiVoice && get(secondaryPlayerOption, 'sources[0].id')
					}
					floName={floName}
					hideHeader={hideHeader}
					videoReady={videoReady}
					width={get(playerDim, 'width', 0)}
					height={get(playerDim, 'height', 0)}
					offsetHeight={get(playerDim, 'offsetHeight', 0)}
					offsetWidth={get(playerDim, 'offsetWidth', 0)}
					createdBy={createdByName}
					createdOn={createdOn}
					volume={volume}
					onPlaybackRatesChange={onPlaybackRatesChange}
					progressRef={progressRef}
					durationRef={durationRef}
					onVolumeChange={onVolumeChange}
					customControlsChildren={customControlsChildren}
					// @ts-ignore
					textTrack={get(mainPlayerOption, 'tracks[0]')}
					onClickProgressBar={onClickProgressBar}
					currentHotspot={hotspot.current}
					videoContainerRef={videoContainerRef}
				>
					<div
						className={classNames(styles.videoContainer, styles.overflowVisible, {
							[styles.interactiveVideoContainer]: interactiveMode,
							[styles.moveThumbnail]: isAudioWaveAvailable,
							[styles.fullScreen]: hideHeader && !internalArticles,
							[styles.withBorder]: withBorder,
							[styles.noHeadeOrFullSCreen]: hideHeader,
							[styles.hideSubtitle]: subTitle,
						})}
						ref={containerRef}
					>
						<div
							className={classNames(styles.brandImageContainer, {
								[styles.brandImageFullscreen]: hideHeader,
								[styles.showingCta]: dialogId,
							})}
							style={{
								width: get(playerDim, 'width', 0),
								height: get(playerDim, 'height', 0) - (offsetTop > 0 ? offsetTop * 2 : 0),
							}}
						>
							<div
								className={styles.brandImageWrapper}
								style={{
									width: '100%',
									height: '100%',
								}}
							>
								{videoReady && !hidePlayer && (
									<BrandImage floId={floId} floIndex={floIndex} />
								)}
								{!isTabView && (
									<DialogComponent
										floId={floId}
										floIndex={floIndex}
										style={{
											width: Math.ceil(playerDim.width),
											height: Math.ceil(playerDim.height),
										}}
									/>
								)}
							</div>
						</div>
						{isPause &&
							get(imageElement, 'time', 0) - currentTimeSeconds < 0.01 &&
							get(imageElement, 'imageUrl') && (
								<ImageElement
									key={`${hasNotPlayed ? 'play' : 'paused'}_${get(imageElement, 'id')}`}
									currentElementIndex={currentElementIndex}
									currentElement={imageElement}
									playerDim={playerDim}
									goToStep={goToStep}
									togglePause={toggleCountDownPause}
									setCountDownPause={setImageCountDownPause}
									hasNotPlayed={hasNotPlayed}
									isWidthLessThan650={isVideoWidthLessThan650}
									elementVoiceResources={elementVoiceResources}
									floId={floId}
									floIndex={floIndex}
								/>
							)}
						{isPause &&
							get(textElement, 'time', 0) - currentTimeSeconds < 0.01 &&
							currentElement &&
							textElement &&
							get(currentElement, 'id', '') === get(textElement, 'id', '') && (
								<TextElement
									key={`${hasNotPlayed ? 'play' : 'paused'}_${get(textElement, 'id')}`}
									currentElementIndex={currentElementIndex}
									currentElement={textElement}
									playerDim={playerDim}
									width={playerDim.width}
									height={playerDim.height}
									goToStep={goToStep}
									togglePause={toggleCountDownPause}
									setCountDownPause={setImageCountDownPause}
									hasNotPlayed={hasNotPlayed}
									isWidthLessThan650={isVideoWidthLessThan650}
									elementVoiceResources={elementVoiceResources}
									floId={floId}
									floIndex={floIndex}
								/>
							)}
						{/* @ts-ignore*/}
						<TextElementShowUntil
							floIndex={floIndex}
							currentTimeSeconds={currentTimeSeconds}
							width={playerDim.width}
							height={playerDim.height}
							videoSize={videoSize}
							isCropped={hotspotCropConfig?.enable}
							// @ts-ignore
							appliedCrop={hotspotCropConfig}
							elementType={FLO_ELEMENT_TEXT_TYPE}
						/>

						{/* @ts-ignore*/}
						<Video
							onLoadedData={onLoadedData}
							//@ts-ignore
							setPlayerRef={setPlayerRef}
							isTabView={isTabView}
							ignoreThumbnail={interactiveMode}
							//@ts-ignore
							nodeRef={screenShareNode}
							addAnnotationHandler={addAnnotationHandler}
							offsetWidth={leftSeekPosition || 0}
							offsetHeight={(playerDim.offsetHeight || 0) - (hideHeader ? 0 : 108)}
							addWrapper
							setError={setError}
							inFullScreen={inFullScreen}
							hasInitialhotspot={get(hotspot.current, 'time', -1) === 0}
							volume={volume}
							withBorder={withBorder}
							floName={floName}
							createdBy={createdByName}
							duration={duration}
							createdOn={createdOn}
							hideHeader={hideHeader}
							internalArticles={internalArticles}
							isAudioWaveAvailable={isAudioWaveAvailable}
							onPlaybackRatesChange={onPlaybackRatesChange}
							setReady={onSetReadyCallback}
							containerRef={videoContainerRef}
							options={mainPlayerOption}
							appliedCrop={appliedCrop}
							//@ts-ignore
							hotSpotCropStyles={hotSpotCropStyles}
							croppedVideoStyles={croppedVideoStyles}
							setCroppedVideoStyles={setCroppedVideoStyles}
							className="screenShareTrack"
							setControls={setScreenShareControls}
							setPlayerDim={setPlayerDimCallback}
							onPlay={onPlay}
							mute={
								isNoVoice ||
								(!isAiVoice && get(secondaryPlayerOption, 'sources[0].id')) ||
								elementsVoiceEnabled
							}
							defaultMute={autoPlay}
							autoPlay={autoPlay && currentElementTime !== 0}
							onPlayRef={onPlayRef}
							onPause={onPause}
							onUpdate={onUpdate}
							onUpdateRef={onUpdateRef}
							onSeeked={onSeeked}
							onSeeking={onSeeking}
							onVolumeChange={onVolumeChange}
							onTimeUpdate={onTimeUpdate}
							onChangeTime={onChangeTime}
							canShowCustomControls
							customControlsChildren={customControlsChildren}
							videoSize={videoSize}
							onEnded={onVideoEnded}
							progressRef={progressRef}
							durationRef={durationRef}
							onKeyBoardProgress={onKeyBoardProgress}
							floId={floId}
							floIndex={floIndex}
							wrapper={showWrapper}
						/>

						{!isAiVoice && (
							<div
								ref={secondaryVideoRef}
								style={{ position: 'absolute', width: '100%', height: '100%' }}
							>
								<Draggable
									position={{
										x: leftPosition,
										y: bottomPosition,
									}}
									bounds={
										secondaryVideoRef.current
											? {
													left: 0,
													top: 0,
													right:
														secondaryVideoRef.current.offsetWidth - videoPlayerDim.width,
													bottom:
														secondaryVideoRef.current.offsetHeight -
														videoPlayerDim.height,
											  }
											: undefined
									}
									onStop={(event, diff) => {
										setLeftPosition(diff.x);
										setBottomPosition(diff.y);
										setVideoPlayerDim((dims) => ({ ...dims, initial: false }));
									}}
									cancel={'.react-resizable-handle'}
								>
									<Resizable
										lockAspectRatio
										className={classNames(styles.resizeContainer, {
											[styles.resizeActive]: !isAnnotationProgress,
										})}
										height={videoPlayerDim.height}
										width={videoPlayerDim.width}
										maxConstraints={[
											miniPlayerMaxDim.width * 0.7,
											miniPlayerMaxDim.height * 0.7,
										]}
										minConstraints={[
											miniPlayerMaxDim.width * 0.2,
											miniPlayerMaxDim.height * 0.2,
										]}
										resizeHandles={['se']}
										handle={
											<span
												style={{
													left: videoPlayerDim.width - 10,
													top: videoPlayerDim.height - 10,
												}}
												className={'react-resizable-handle'}
											/>
										}
										onResize={onPresenterVideoResize}
									>
										<div
											className={classNames({
												[styles.hidden]:
													!get(secondaryPlayerOption, 'sources[0].id') ||
													hidePresenterVideo ||
													isNoVoice,
												hiddenVideo: videoPlayerDim.width <= 16 * 4,
											})}
											data-video-width={videoPlayerDim.width <= 16 * 4}
											style={{
												bottom: 0,
												left: 0,
												position: 'absolute',
												width: '100%',
											}}
										>
											{/*@ts-ignore*/}
											<Video
												addWrapper
												setReady={onVideoSetReadyCallback}
												mute={
													isNoVoice ||
													!(!isAiVoice && get(secondaryPlayerOption, 'sources[0].id')) ||
													elementsVoiceEnabled
												}
												style={presenterBaseStyles}
												//@ts-ignore
												setError={setError}
												//@ts-ignore
												nodeRef={videoNode}
												defaultMute={autoPlay}
												autoPlay={autoPlay && currentElementIndex === -1}
												ignoreThumbnail
												options={secondaryPlayerOption}
												ignoreCropAndTrim
												className={classNames('videoTrack', {})}
												setControls={setVideoControls}
												defaultOptions={presenterVideoOptions}
												floId={floId}
												floIndex={floIndex}
											/>
										</div>
									</Resizable>
								</Draggable>
							</div>
						)}
						{/*@ts-ignore*/}
						<Video
							//@ts-ignore
							nodeRef={audioNode}
							options={audioOptions}
							ignoreCropAndTrim
							className="audioTrack"
							setControls={setAudioControls}
							defaultOptions={defaultControls}
							floId={floId}
							floIndex={floIndex}
						/>
						{isAudioWaveAvailable && (
							<div
								className={classNames(styles.playerAmplitudeContainer, {
									[styles.amplitudeView]: isHoveringOnProgress,
								})}
								style={{
									top: playerDim.height - 75,
									width: playerDim.width + 10,
									left: leftSeekPosition,
								}}
							>
								<Amplitude data={amplitudeData} />
							</div>
						)}
						{subTitle && (
							<div className={styles.elementVoiceSubtitleWrapper}>
								<div className={styles.elementVoiceSubtitle}>{subTitle}</div>
							</div>
						)}
						{(showElementVoiceIcon || showVoiceIcon) && (
							<Tooltip
								title={elementVoiceTooltipTitle || ''}
								key={elementVoiceTooltipTitle}
								open={false}
								//@ts-ignore
								arrow
								classes={{ popper: styles.tooltip }}
							>
								<div className={styles.elementVoiceIconWrapper}>
									<IconButton
										className={styles.elementVoiceIconButton}
										onClick={
											isElementVoicePlaying || showVoiceIcon
												? onElementVoiceMuteClick
												: onReplayClick
										}
									>
										{isElementVoicePlaying || showVoiceIcon ? (
											<>
												{volume === 0 ? (
													<IconVolumeOff
														className={classNames(styles.muteIcon, styles.voiceIcon)}
														size={20}
													/>
												) : (
													<IconVolume
														className={classNames(styles.muteIcon, styles.voiceIcon)}
														size={20}
													/>
												)}
												<div
													className={classNames(
														styles.elementVoiceIcon,
														styles.voiceIcon
													)}
												>
													<Player
														autoplay
														loop
														src={audioPlaying}
														style={{ height: 40, width: 40 }}
														className={styles.elementVoiceLottieIcon}
													/>
												</div>
											</>
										) : (
											<IconRotate2
												className={classNames(styles.restartVoiceIcon, styles.voiceIcon)}
											/>
										)}
									</IconButton>
								</div>
							</Tooltip>
						)}
					</div>
				</CustomInVideoControls>
				<VideoBlurs
					floIndex={floIndex}
					currentTimeSeconds={currentTimeSeconds}
					width={playerDim.width}
					height={playerDim.height}
					videoSize={videoSize}
					isCropped={hotspotCropConfig?.enable}
					// @ts-ignore
					appliedCrop={hotspotCropConfig}
					elementType={FLO_ELEMENT_BLUR_TYPE}
					stylesObj={{
						transform: showWrapper
							? `translateY(${isTabView && !isEmbed ? 32 : 16}px)`
							: 0,
					}}
				/>
				<VideoSpotlights
					floIndex={floIndex}
					currentTimeSeconds={currentTimeSeconds}
					width={playerDim.width}
					height={playerDim.height}
					videoSize={videoSize}
					isCropped={hotspotCropConfig?.enable}
					// @ts-ignore
					appliedCrop={hotspotCropConfig}
					elementType={FLO_ELEMENT_SPOTLIGHT_TYPE}
					stylesObj={{
						transform: showWrapper
							? `translateY(${isTabView && !isEmbed ? 32 : 16}px)`
							: 0,
					}}
				/>
				{!hasNotPlayed && !showAppComment && gotoElementIndex === -1 && (
					<ClickEventActivity
						isTabView={isTabView}
						floId={floId}
						updateActivityPosition={setActivityPosition}
						isSeeking={isSeeking}
						isInteractive={false}
						croppedVideoStyles={croppedVideoStyles}
						// @ts-ignore
						hotSpotCropStyles={hotSpotCropStyles}
						width={playerDim.width}
						height={playerDim.height}
						offsetWidth={offsetLeft}
						offsetHeight={offsetTop}
						activity={activity}
						hideHeader={hideHeader}
						withBorder={withBorder}
						internalArticles={internalArticles}
						isCropped={hotspotCropConfig?.enable}
						// @ts-ignore
						appliedCrop={hotspotCropConfig}
						floIndex={floIndex}
					/>
				)}
				{!hasNotPlayed && !showAppComment && gotoElementIndex === -1 && (
					<ClickEventActivity
						updateActivityPosition={setActivityPosition}
						floId={floId}
						isTabView={isTabView}
						isSeeking={isSeeking}
						isInteractive={true}
						croppedVideoStyles={croppedVideoStyles}
						// @ts-ignore
						hotSpotCropStyles={hotSpotCropStyles}
						width={playerDim.width}
						height={playerDim.height}
						offsetWidth={offsetLeft}
						offsetHeight={offsetTop}
						activity={hotspot.current}
						hideHeader={hideHeader}
						withBorder={withBorder}
						internalArticles={internalArticles}
						isCropped={hotspotCropConfig?.enable}
						// @ts-ignore
						appliedCrop={hotspotCropConfig}
						onClick={onHotSpotClick}
						videoContainerRef={videoContainerRef}
						floIndex={floIndex}
					/>
				)}
				{enableAddComment.visible && !isFloClosed && (
					<div
						className={classNames(
							styles.floatingCommentContainer,
							enableAddComment.className
						)}
						// @ts-ignore
						ref={floatingRef}
						style={{
							top: offsetTop + enableAddComment.top,
							left: leftSeekPosition + enableAddComment.left,
						}}
					>
						<div className={styles.colorStrip}></div>
						<div className={styles.commentContainer}>
							<IconButton
								className={styles.closeButton}
								aria-label="close"
								onClick={handleClose}
								sx={{
									position: 'absolute',
									right: 24,
									top: 24,
								}}
							>
								<BlackCloseIcon className={styles.closeIcon} />
							</IconButton>
							<div className={styles.messageContainer}>
								<TextareaAutosize
									className={styles.textArea}
									aria-label={PUBLISHED_FLO_COMMENT_PLACEHOLDER_QUESTION}
									minRows={3}
									onChange={onQuestionChange}
									placeholder={PUBLISHED_FLO_COMMENT_PLACEHOLDER_QUESTION}
									autoFocus
									onKeyDown={onQuestionKeyDownCallback}
								/>
							</div>
							<div className={styles.messageContainer}>
								<TextareaAutosize
									className={styles.input}
									aria-label={PUBLISHED_FLO_COMMENT_PLACEHOLDER_NAME}
									minRows={1}
									maxRows={1}
									defaultValue={enableAddComment.name}
									onChange={onNameChange}
									placeholder={PUBLISHED_FLO_COMMENT_PLACEHOLDER_NAME}
									onKeyDown={onNameKeyDownCallback}
								/>
							</div>
							<div className={styles.messageContainer}>
								<TextareaAutosize
									className={styles.input}
									aria-label={PUBLISHED_FLO_COMMENT_PLACEHOLDER_EMAIL}
									minRows={1}
									maxRows={1}
									defaultValue={enableAddComment.email}
									onChange={onEmailChange}
									placeholder={PUBLISHED_FLO_COMMENT_PLACEHOLDER_EMAIL}
									onKeyDown={onEmailKeyDownCallback}
								/>
							</div>
							<div className={styles.messageContainer}>
								<img
									alt="Floik Logo"
									src={brandImage}
									className={classNames(styles.logo, {
										[styles.hide]: !brandImage,
									})}
								/>
								<Button
									variant="contained"
									disableElevation
									className={styles.floatingControlsSendButton}
									onClick={sendAnnotationComment}
									disabled={
										!validEmail || !get(enableAddComment, 'question', '').trim().length
									}
								>
									Send
								</Button>
							</div>
						</div>
					</div>
				)}
				{/*@ts-ignore*/}
				{!hasError && (allowComment || showAppComment) && !isTouchDevice && videoSize && (
					<Annotations
						// @ts-ignore
						currentUser={currentUser}
						videoSize={videoSize}
						doNotSetTop={withBorder || (hideHeader && !internalArticles)}
						onDone={addAnnotationHandler}
						allowComment={showAppComment}
						addingComment={enableAddComment.visible}
						actionType={enableAreaSelection ? 'crop' : active}
						isFloClosed={isFloClosed}
						// @ts-ignore
						player={screenShareNode.current}
						continuePlay={onContinuePlay}
						enable={active !== 'none' || enableAreaSelection}
						onAnnotationProgress={setAnnotationProgress}
						showData={currentAnnotations}
						//@ts-ignore
						elements={annotations}
						onAnnotationClick={onAnnotationClick}
						width={playerDim.width}
						height={playerDim.height}
						offsetWidth={leftSeekPosition - 13}
						offsetHeight={playerDim.offsetHeight}
						onAreaSelected={onAreaSelected}
						areaSelected={areaSelected}
						floId={floId}
						floIndex={floIndex}
					/>
				)}
				{isTabView && !showAppComment && (
					<Fragment>
						{dialogId === 'start' &&
							!get(floElements, 'info_ctastart[0]') &&
							screenShareControls?.current?.currentTime() === 0 && (
								<div className={classNames(styles.dialogWrapper)}>
									<div className={styles.floNameWrapper}>
										<div className={styles.floName} title={floName}>
											{floName}
										</div>
									</div>
									<Button
										variant="contained"
										autoFocus={true}
										disableFocusRipple
										disableElevation
										className={styles.startButton}
										onClick={() => {
											if (isFunction(dialogOnStartConfirm)) {
												// @ts-ignore
												dialogOnStartConfirm();
											}
											if (ctaId) {
												dispatch(
													trackCtaAction({
														id: ctaId,
														skip: 'skip-n-continue',
														buttonProperties: [],
														floId,
														floIndex,
													})
												);
											}
										}}
									>
										{FLO_START_BUTTON_TEXT}
									</Button>
								</div>
							)}
					</Fragment>
				)}
			</div>
		</>
	);
};

export default VideoPlayer;
